import { type FC, Fragment, useEffect, useMemo, useState } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import { SearchInput, FilterBar, PaymentStatusBadge, Drawer, useToast } from '@/components/base';
import { useAppContext } from '@/contexts/AppContext';
import { useCases } from '@/hooks/useCases';
import { useFeeRules } from '@/hooks/useFeeRules';
import { useAuth } from '@/hooks/useAuth';
import {
  type CreatePaymentInput,
  type Payment,
  type PaymentChargeType,
  type PaymentInstallment,
  type SucumbencyBasisType,
  type SucumbencyCalculationType,
  usePayments,
} from '@/hooks/usePayments';
import { type FinancialAccount, type PaymentSettlement, useFinanceSettlements } from '@/hooks/useFinanceSettlements';
import { type FinancialTransaction, useFinancialLedger } from '@/hooks/useFinancialLedger';
import { type FinancialStatementEntry, type ReconciliationCandidate, useFinancialReconciliation } from '@/hooks/useFinancialReconciliation';
import { useFinancialMonthClosing } from '@/hooks/useFinancialMonthClosing';
import { useFinancialReports } from '@/hooks/useFinancialReports';
import { centsToDecimalString, centsToMajorNumber, formatBRLCents, parseMoneyToCents } from '@/lib/money';

type ViewMode = 'lista' | 'produto' | 'caso' | 'vencidas' | 'avencer' | 'Vencidas';

type PaymentFormState = {
  clientId: string;
  caseId: string;
  feeRuleId: string;
  chargeType: PaymentChargeType;

  sucumbencyCalculationType:
    SucumbencyCalculationType;

  sucumbencyBasisType:
    SucumbencyBasisType;

  sucumbencyPercentage:
    string;

  sucumbencyBasisAmount:
    string;

  title: string;
  description: string;
  totalAmount: string;
  entryAmount: string;
  installmentCount: string;
  installmentAmount: string;
  successFeePercentage: string;
  paidAmount: string;
  status: string;
  paymentMethod: string;
  contractDate: string;
  firstDueDate: string;
  dueDay: string;
  responsibleUser: string;
  notes: string;
};

type SettlementFormState = {
  paymentId: string;
  installmentId: string;
  amount: string;
  occurredAt: string;
  paymentMethod: string;
  financialAccountId: string;
  receiptReference: string;
  notes: string;
  idempotencyKey: string;
};

type FinancialAccountFormState = {
  name: string;
  accountType: string;
  institution: string;
  openingBalance: string;
  openingBalanceDate: string;
  notes: string;
};

type FinancialMovementFormState = {
  transactionType:
    | 'income'
    | 'expense'
    | 'transfer';

  amount: string;

  accountId: string;
  destinationAccountId: string;

  categoryId: string;

  occurredAt: string;
  description: string;

  clientId: string;
  caseId: string;

  reference: string;
  notes: string;

  idempotencyKey: string;
};

const financialTransactionTypeOptions = [
  {
    label: 'Receita',
    value: 'income',
  },
  {
    label: 'Despesa',
    value: 'expense',
  },
  {
    label: 'Transferência',
    value: 'transfer',
  },
];

const financialTransactionTypeLabel =
  (value: string) =>
    financialTransactionTypeOptions.find(
      (option) =>
        option.value === value
    )?.label ??
    (
      value === 'reversal'
        ? 'Estorno'
        : value
    );

const financialChargeTypeLabel =
  (value: string) => {
    switch (value) {
      case 'contractual_fee':
        return 'Honorários contratuais';

      case 'success_fee':
        return 'Honorários de êxito';

      case 'sucumbency':
        return 'Sucumbência';

      case 'reimbursement':
        return 'Reembolso';

      case 'other':
        return 'Outros';

      default:
        return value;
    }
  };

const financialAccountTypeOptions = [
  { label: 'Conta bancária', value: 'bank' },
  { label: 'Caixa / dinheiro', value: 'cash' },
  { label: 'Carteira digital', value: 'digital_wallet' },
  { label: 'Outra', value: 'other' },
];

const financialAccountTypeLabel = (
  value: string
) =>
  financialAccountTypeOptions.find(
    (option) =>
      option.value === value
  )?.label ?? value;

const paymentStatusFilterOptions = [
  { label: 'Pendente', value: 'pending' },
  { label: 'Ativo', value: 'active' },
  { label: 'Pago', value: 'paid' },
  { label: 'Vencido', value: 'overdue' },
  { label: 'Cancelado', value: 'cancelled' },
  { label: 'Arquivado', value: 'archived' },
];

const paymentChargeTypeFilterOptions = [
  {
    label: 'Honorários contratuais',
    value: 'contractual_fee',
  },
  {
    label: 'Honorários de êxito',
    value: 'success_fee',
  },
  {
    label: 'Sucumbência',
    value: 'sucumbency',
  },
  {
    label: 'Reembolso',
    value: 'reimbursement',
  },
  {
    label: 'Outros',
    value: 'other',
  },
];

const paymentMethodOptions = [
  { label: 'Pix', value: 'pix' },
  { label: 'Transferência bancária', value: 'bank_transfer' },
  { label: 'Cartão de crédito', value: 'credit_card' },
  { label: 'Cartão de débito', value: 'debit_card' },
  { label: 'Dinheiro', value: 'cash' },
  { label: 'Boleto', value: 'boleto' },
  { label: 'Outro', value: 'other' },
];

const installmentStatusLabelMap: Record<string, string> = {
  pending: 'Aguardando pagamento',
  paid: 'Pago',
  overdue: 'Vencido',
  cancelled: 'Cancelado',
};

const formatCurrencyCents = (cents: number) => formatBRLCents(cents);

const splitMoneyCents = (totalCents: number, count: number): number[] => {
  if (!Number.isSafeInteger(totalCents) || totalCents < 0) {
    return [];
  }

  if (!Number.isInteger(count) || count < 1) {
    return [];
  }

  const base = Math.floor(totalCents / count);
  const remainder = totalCents % count;

  return Array.from(
    { length: count },
    (_, index) => base + (index < remainder ? 1 : 0)
  );
};

const resolvePaymentFormTotalCents = (
  form: PaymentFormState
) => {
  if (
    form.chargeType !==
      'sucumbency' ||
    form.sucumbencyCalculationType ===
      'fixed'
  ) {
    return parseMoneyToCents(
      form.totalAmount
    );
  }

  const basisAmountCents =
    parseMoneyToCents(
      form.sucumbencyBasisAmount
    );

  const percentage =
    toNullableNumber(
      form.sucumbencyPercentage
    );

  if (
    basisAmountCents === null ||
    basisAmountCents <= 0 ||
    percentage === null ||
    percentage <= 0 ||
    percentage > 100
  ) {
    return null;
  }

  const result =
    Math.round(
      (
        basisAmountCents *
        percentage
      ) / 100
    );

  return Number.isSafeInteger(result)
    ? result
    : null;
};

const downloadJsonFile = (filename: string, data: unknown) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener noreferrer';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const toNullableText = (value: unknown) => {
  if (value === undefined || value === null) {
    return null;
  }

  const text = String(value).trim();
  return text === '' ? null : text;
};

const toNullableNumber = (value: unknown) => {
  if (value === undefined || value === null || value === '') {
    return null;
  }

  const number = Number(String(value).replace(',', '.'));
  return Number.isFinite(number) ? number : null;
};

const toNullableInteger = (value: unknown) => {
  if (value === undefined || value === null || value === '') {
    return null;
  }

  const number = Number(String(value).replace(',', '.'));
  return Number.isInteger(number) ? number : null;
};

const normalizeDateInput = (value: unknown) => {
  const text = toNullableText(value);
  if (!text) {
    return null;
  }

  const parsed = new Date(`${text}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : text;
};

const formatDate = (value: string | null) => {
  if (!value) {
    return '—';
  }

  const parsed = new Date(value.includes('T') ? value : `${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleDateString('pt-BR');
};

const todayInputDate = () => new Date().toISOString().slice(0, 10);

const buildEmptySettlementForm = (): SettlementFormState => ({
  paymentId: '',
  installmentId: '',
  amount: '',
  occurredAt: todayInputDate(),
  paymentMethod: 'pix',
  financialAccountId: '',
  receiptReference: '',
  notes: '',
  idempotencyKey: '',
});

const buildEmptyFinancialMovementForm =
  (): FinancialMovementFormState => ({
    transactionType:
      'expense',

    amount: '',

    accountId: '',
    destinationAccountId: '',

    categoryId: '',

    occurredAt:
      todayInputDate(),

    description: '',

    clientId: '',
    caseId: '',

    reference: '',
    notes: '',

    idempotencyKey:
      crypto.randomUUID(),
  });

const buildEmptyFinancialAccountForm =
  (): FinancialAccountFormState => ({
    name: '',
    accountType: 'bank',
    institution: '',
    openingBalance: '0.00',
    openingBalanceDate:
      todayInputDate(),
    notes: '',
  });

const buildFinancialAccountForm = (
  account: FinancialAccount
): FinancialAccountFormState => ({
  name: account.name,
  accountType:
    account.accountType,
  institution:
    account.institution ?? '',
  openingBalance:
    centsToDecimalString(
      account.openingBalanceCents
    ),
  openingBalanceDate:
    account.openingBalanceDate ?? '',
  notes:
    account.notes ?? '',
});

const buildEmptyPaymentForm = (): PaymentFormState => ({
  clientId: '',
  caseId: '',
  feeRuleId: '',
  chargeType: 'contractual_fee',

  sucumbencyCalculationType:
    'percentage',

  sucumbencyBasisType:
    'case_value',

  sucumbencyPercentage:
    '',

  sucumbencyBasisAmount:
    '',

  title: '',
  description: '',
  totalAmount: '',
  entryAmount: '0',
  installmentCount: '1',
  installmentAmount: '',
  successFeePercentage: '',
  paidAmount: '0',
  status: 'pending',
  paymentMethod: 'pix',
  contractDate: todayInputDate(),
  firstDueDate: '',
  dueDay: '',
  responsibleUser: '',
  notes: '',
});

const buildPaymentFormFromPayment = (payment: Payment): PaymentFormState => ({
  clientId: payment.clientId,
  caseId: payment.caseId ?? '',
  feeRuleId: payment.feeRuleId ?? '',
  chargeType:
    payment.chargeType ??
    'contractual_fee',

  sucumbencyCalculationType:
    payment.sucumbencyCalculationType ??
    (
      payment.chargeType ===
        'sucumbency'
        ? 'fixed'
        : 'percentage'
    ),

  sucumbencyBasisType:
    payment.sucumbencyBasisType ??
    'case_value',

  sucumbencyPercentage:
    payment.sucumbencyPercentage ===
      null
      ? ''
      : String(
          payment.sucumbencyPercentage
        ),

  sucumbencyBasisAmount:
    payment.sucumbencyBasisAmountCents ===
      null
      ? ''
      : centsToDecimalString(
          payment.sucumbencyBasisAmountCents
        ),

  title: payment.title,
  description: payment.description ?? '',
  totalAmount: centsToDecimalString(payment.totalAmountCents),
  entryAmount:
    payment.entryAmountCents === null
      ? ''
      : centsToDecimalString(payment.entryAmountCents),
  installmentCount: String(
    (payment.installmentCount ?? payment.installments.length) || 1
  ),
  installmentAmount:
    payment.installmentAmountCents === null
      ? ''
      : centsToDecimalString(payment.installmentAmountCents),
  successFeePercentage:
    payment.successFeePercentage !== null &&
    payment.successFeePercentage !== undefined
      ? String(payment.successFeePercentage)
      : '',
  paidAmount:
    payment.paidAmountCents === null
      ? ''
      : centsToDecimalString(payment.paidAmountCents),
  status: payment.status || 'pending',
  paymentMethod: payment.paymentMethod ?? 'pix',
  contractDate: payment.contractDate ?? todayInputDate(),
  firstDueDate: payment.firstDueDate ?? '',
  dueDay:
    payment.dueDay !== null && payment.dueDay !== undefined
      ? String(payment.dueDay)
      : '',
  responsibleUser: payment.responsibleUser ?? '',
  notes: payment.notes ?? '',
});

const buildCreatePaymentInput = (form: PaymentFormState): CreatePaymentInput => {
  const totalAmountCents = Math.max(
    0,
    resolvePaymentFormTotalCents(
      form
    ) ?? 0
  );

  const entryAmountCents = Math.max(
    0,
    parseMoneyToCents(form.entryAmount) ?? 0
  );

  const installmentCount = Math.max(
    1,
    toNullableInteger(form.installmentCount) ?? 1
  );

  const remainingAmountCents = Math.max(
    0,
    totalAmountCents - entryAmountCents
  );

  const installmentAmountsCents = splitMoneyCents(
    remainingAmountCents,
    installmentCount
  );

  const installmentAmountCents =
    installmentAmountsCents[0] ?? 0;

  const paidAmountCents = Math.max(
    0,
    parseMoneyToCents(form.paidAmount) ?? 0
  );

  const normalizedContractDate =
    normalizeDateInput(form.contractDate);

  const normalizedFirstDueDate =
    normalizeDateInput(form.firstDueDate);

  const dueDay = normalizedFirstDueDate
    ? Number(normalizedFirstDueDate.split('-')[2])
    : null;

  const normalizedDueDay =
    Number.isFinite(dueDay) ? dueDay : null;

  return {
    clientId: form.clientId,
    caseId: toNullableText(form.caseId),
    feeRuleId: toNullableText(form.feeRuleId),
    chargeType: form.chargeType,

    sucumbencyCalculationType:
      form.chargeType ===
        'sucumbency'
        ? form.sucumbencyCalculationType
        : null,

    sucumbencyBasisType:
      form.chargeType ===
          'sucumbency' &&
        form.sucumbencyCalculationType ===
          'percentage'
        ? form.sucumbencyBasisType
        : null,

    sucumbencyPercentage:
      form.chargeType ===
          'sucumbency' &&
        form.sucumbencyCalculationType ===
          'percentage'
        ? toNullableNumber(
            form.sucumbencyPercentage
          )
        : null,

    sucumbencyBasisAmountCents:
      form.chargeType ===
          'sucumbency' &&
        form.sucumbencyCalculationType ===
          'percentage'
        ? parseMoneyToCents(
            form.sucumbencyBasisAmount
          )
        : null,

    title: form.title.trim(),
    description: toNullableText(form.description),

    totalAmount: centsToMajorNumber(totalAmountCents),
    totalAmountCents,

    entryAmount: centsToMajorNumber(entryAmountCents),
    entryAmountCents,

    installmentCount,

    installmentAmount:
      centsToMajorNumber(installmentAmountCents),
    installmentAmountCents,

    successFeePercentage:
      toNullableNumber(form.successFeePercentage),

    paidAmount: centsToMajorNumber(paidAmountCents),
    paidAmountCents,

    status: form.status || 'pending',
    paymentMethod:
      toNullableText(form.paymentMethod) ?? 'pix',
    contractDate:
      normalizedContractDate ?? todayInputDate(),
    firstDueDate: normalizedFirstDueDate,
    dueDay: normalizedDueDay,
    responsibleUser:
      toNullableText(form.responsibleUser),
    notes: toNullableText(form.notes),
  };
};

const buildPaymentUpdateInputFromPayment = (
  payment: Payment,
  overrides: Partial<CreatePaymentInput> = {}
): CreatePaymentInput => ({
  clientId: payment.clientId,
  caseId: payment.caseId,
  feeRuleId: payment.feeRuleId,
  chargeType: payment.chargeType,

  sucumbencyCalculationType:
    payment.sucumbencyCalculationType,

  sucumbencyBasisType:
    payment.sucumbencyBasisType,

  sucumbencyPercentage:
    payment.sucumbencyPercentage,

  sucumbencyBasisAmountCents:
    payment.sucumbencyBasisAmountCents,

  title: payment.title.trim(),
  description: payment.description,

  totalAmount: payment.totalAmount,
  totalAmountCents: payment.totalAmountCents,

  entryAmount: payment.entryAmount,
  entryAmountCents: payment.entryAmountCents,

  installmentCount:
    payment.installmentCount ??
    Math.max(payment.installments.length, 1),

  installmentAmount: payment.installmentAmount,
  installmentAmountCents:
    payment.installmentAmountCents,

  successFeePercentage:
    payment.successFeePercentage,

  paidAmount: payment.paidAmount,
  paidAmountCents: payment.paidAmountCents,

  status: payment.status,
  paymentMethod: payment.paymentMethod,
  contractDate: payment.contractDate,
  firstDueDate: payment.firstDueDate,
  dueDay: payment.dueDay,
  responsibleUser: payment.responsibleUser,
  notes: payment.notes,

  ...overrides,
});

const getInstallmentStatusLabel = (status: string) => installmentStatusLabelMap[status] ?? status;

const isArchivedOrCancelledPayment = (payment: Payment) => payment.status === 'archived' || payment.status === 'cancelled';

const hasOverdueInstallment = (payment: Payment) => payment.installments.some((installment) => installment.status === 'overdue');

const hasUnpaidInstallment = (payment: Payment) => payment.installments.some((installment) => installment.status !== 'paid');

const compareText = (left: string | null | undefined, right: string | null | undefined) =>
  (left ?? '').localeCompare(right ?? '', 'pt-BR', { sensitivity: 'base' });

const compareDate = (left: string | null | undefined, right: string | null | undefined) => {
  const leftTime = left ? Date.parse(left.includes('T') ? left : `${left}T00:00:00`) : Number.NaN;
  const rightTime = right ? Date.parse(right.includes('T') ? right : `${right}T00:00:00`) : Number.NaN;

  if (Number.isFinite(leftTime) && Number.isFinite(rightTime) && leftTime !== rightTime) {
    return leftTime - rightTime;
  }

  if (Number.isFinite(leftTime) && !Number.isFinite(rightTime)) {
    return -1;
  }

  if (!Number.isFinite(leftTime) && Number.isFinite(rightTime)) {
    return 1;
  }

  return 0;
};

const getPaymentPrimaryDueDate = (payment: Payment) =>
  payment.installments
    .map((installment) => installment.dueDate)
    .filter((dueDate): dueDate is string => Boolean(dueDate))
    .sort((left, right) => compareDate(left, right))[0] ?? payment.firstDueDate ?? null;

const FinancePage: FC = () => {
  const { addToast } = useToast();
  const { user: authUser } = useAuth();
  const { clients, clientsLoading, users } = useAppContext();
  const { cases, isLoadingCases } = useCases();
  const { feeRules, isLoadingFeeRules } = useFeeRules();
  const {
    payments,
    isLoadingPayments,
    paymentsError,
    loadPayments,
    createPayment,
    updatePayment,
    archivePayment,
  } = usePayments();

  const {
    financialAccounts,
    allFinancialAccounts,
    isLoadingFinancialAccounts,
    isLoadingAllFinancialAccounts,
    financialAccountsError,
    financialAccountsManagementError,
    loadFinancialAccounts,
    loadAllFinancialAccounts,
    createFinancialAccount,
    updateFinancialAccount,
    createSettlement,
    loadSettlements,
    reverseSettlement,
  } = useFinanceSettlements();

  const {
    financialCategories,
    financialTransactions,
    isLoadingFinancialCategories,
    isLoadingFinancialTransactions,
    financialLedgerError,
    loadFinancialTransactions,
    createFinancialTransaction,
    reverseFinancialTransaction,
  } = useFinancialLedger();

  const {
    statementEntries,
    isLoadingStatementEntries,
    reconciliationError,
    loadStatementEntries,
    importStatementEntries,
    loadCandidates,
    createReconciliationMatch,
  } = useFinancialReconciliation();

  const {
    financialMonthPeriods,
    selectedFinancialMonth,
    financialMonthEvents,
    financialMonthSnapshots,
    isLoadingFinancialMonth,
    financialMonthError,
    loadFinancialMonthPeriods,
    loadFinancialMonthDetail,
    closeFinancialMonth,
    reopenFinancialMonth,
  } = useFinancialMonthClosing();

  const {
    financialReport,
    isLoadingFinancialReport,
    financialReportError,
    loadFinancialReport,
  } = useFinancialReports();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [
    chargeTypeFilter,
    setChargeTypeFilter,
  ] = useState('');

  const [viewMode, setViewMode] = useState<ViewMode>('lista');
  const [showNewCharge, setShowNewCharge] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [paymentBeingEdited, setPaymentBeingEdited] = useState<Payment | null>(null);
  const [isSavingPayment, setIsSavingPayment] = useState(false);
  const [paymentActionId, setPaymentActionId] = useState<string | null>(null);
  const [installmentActionId, setInstallmentActionId] = useState<string | null>(null);
  const [newCharge, setNewCharge] = useState<PaymentFormState>(buildEmptyPaymentForm);

  const [showSettlementDrawer, setShowSettlementDrawer] = useState(false);
  const [settlementPayment, setSettlementPayment] = useState<Payment | null>(null);
  const [settlementForm, setSettlementForm] = useState<SettlementFormState>(buildEmptySettlementForm);
  const [isSavingSettlement, setIsSavingSettlement] = useState(false);

  const [
    settlementHistoryByPaymentId,
    setSettlementHistoryByPaymentId,
  ] = useState<Record<string, PaymentSettlement[]>>({});

  const [
    settlementHistoryLoadingId,
    setSettlementHistoryLoadingId,
  ] = useState<string | null>(null);

  const [
    reversingSettlementId,
    setReversingSettlementId,
  ] = useState<string | null>(null);

  const [
    showFinancialAccountDrawer,
    setShowFinancialAccountDrawer,
  ] = useState(false);

  const [
    financialAccountBeingEdited,
    setFinancialAccountBeingEdited,
  ] = useState<FinancialAccount | null>(null);

  const [
    financialAccountForm,
    setFinancialAccountForm,
  ] = useState<FinancialAccountFormState>(
    buildEmptyFinancialAccountForm
  );

  const [
    isSavingFinancialAccount,
    setIsSavingFinancialAccount,
  ] = useState(false);

  const [
    showFinancialMovementDrawer,
    setShowFinancialMovementDrawer,
  ] = useState(false);

  const [
    financialMovementForm,
    setFinancialMovementForm,
  ] = useState<FinancialMovementFormState>(
    buildEmptyFinancialMovementForm
  );

  const [
    isSavingFinancialMovement,
    setIsSavingFinancialMovement,
  ] = useState(false);

  const [
    reversingFinancialTransactionId,
    setReversingFinancialTransactionId,
  ] = useState<string | null>(null);

  const [
    ledgerSearch,
    setLedgerSearch,
  ] = useState('');

  const [
    ledgerTypeFilter,
    setLedgerTypeFilter,
  ] = useState('');

  const [
    ledgerAccountFilter,
    setLedgerAccountFilter,
  ] = useState('');

  const [
    ledgerCategoryFilter,
    setLedgerCategoryFilter,
  ] = useState('');

  const [
    ledgerClientFilter,
    setLedgerClientFilter,
  ] = useState('');

  const [
    ledgerCaseFilter,
    setLedgerCaseFilter,
  ] = useState('');

  const [
    ledgerFromFilter,
    setLedgerFromFilter,
  ] = useState('');

  const [
    ledgerToFilter,
    setLedgerToFilter,
  ] = useState('');

  const [
    reconciliationAccountFilter,
    setReconciliationAccountFilter,
  ] = useState('');

  const [
    reconciliationStatusFilter,
    setReconciliationStatusFilter,
  ] = useState('');

  const [
    reconciliationFromFilter,
    setReconciliationFromFilter,
  ] = useState('');

  const [
    reconciliationToFilter,
    setReconciliationToFilter,
  ] = useState('');

  const [
    showStatementEntryDrawer,
    setShowStatementEntryDrawer,
  ] = useState(false);

  const [
    statementDirection,
    setStatementDirection,
  ] = useState<
    'credit' | 'debit'
  >('debit');

  const [
    statementAccountId,
    setStatementAccountId,
  ] = useState('');

  const [
    statementPostedAt,
    setStatementPostedAt,
  ] = useState(
    todayInputDate()
  );

  const [
    statementAmount,
    setStatementAmount,
  ] = useState('');

  const [
    statementDescription,
    setStatementDescription,
  ] = useState('');

  const [
    statementReference,
    setStatementReference,
  ] = useState('');

  const [
    statementBalanceAfter,
    setStatementBalanceAfter,
  ] = useState('');

  const [
    isSavingStatementEntry,
    setIsSavingStatementEntry,
  ] = useState(false);

  const [
    showReconciliationDrawer,
    setShowReconciliationDrawer,
  ] = useState(false);

  const [
    selectedStatementEntry,
    setSelectedStatementEntry,
  ] = useState<
    FinancialStatementEntry | null
  >(null);

  const [
    reconciliationCandidates,
    setReconciliationCandidates,
  ] = useState<
    ReconciliationCandidate[]
  >([]);

  const [
    isLoadingCandidates,
    setIsLoadingCandidates,
  ] = useState(false);

  const [
    matchingCandidateId,
    setMatchingCandidateId,
  ] = useState<string | null>(
    null
  );

  const [
    reconciliationCandidateMode,
    setReconciliationCandidateMode,
  ] = useState<
    'automatic' | 'manual'
  >('automatic');

  const [
    reconciliationManualSearch,
    setReconciliationManualSearch,
  ] = useState('');

  const [
    financialClosingPeriod,
    setFinancialClosingPeriod,
  ] = useState(
    todayInputDate().slice(
      0,
      7
    )
  );

  const [
    isProcessingFinancialClosing,
    setIsProcessingFinancialClosing,
  ] = useState(false);

  const [
    financialReportFrom,
    setFinancialReportFrom,
  ] = useState(
    `${todayInputDate().slice(0, 4)}-01-01`
  );

  const [
    financialReportTo,
    setFinancialReportTo,
  ] = useState(
    todayInputDate()
  );

  const [
    financialReportAccountId,
    setFinancialReportAccountId,
  ] = useState('');

  const clientOptions = useMemo(
    () =>
      clients.map((client) => ({
        value: client.id,
        label: client.name,
      })),
    [clients]
  );

  const caseOptions = useMemo(
    () =>
      cases
        .filter((caseItem) => !newCharge.clientId || caseItem.clientId === newCharge.clientId)
        .map((caseItem) => ({
          value: caseItem.id,
          label: caseItem.title || 'Sem nome',
        })),
    [cases, newCharge.clientId]
  );

  const feeRuleOptions = useMemo(
    () =>
      feeRules
        .filter((rule) => rule.isActive)
        .map((rule) => ({
          value: rule.id,
          label: `${rule.name}${rule.legalProductName ? ` • ${rule.legalProductName}` : ''}`,
        })),
    [feeRules]
  );

  const responsibleUserOptions = useMemo(
    () =>
      users.map((user) => ({
        value: user.name,
        label: user.name,
      })),
    [users]
  );

  const financialMovementCategories =
    useMemo(
      () =>
        financialCategories.filter(
          (category) =>
            financialMovementForm.transactionType !==
              'transfer' &&
            (
              category.categoryType ===
                financialMovementForm.transactionType ||
              category.categoryType ===
                'both'
            )
        ),
      [
        financialCategories,
        financialMovementForm.transactionType,
      ]
    );

  const financialMovementCaseOptions =
    useMemo(
      () =>
        cases.filter(
          (caseItem) =>
            !financialMovementForm.clientId ||
            caseItem.clientId ===
              financialMovementForm.clientId
        ),
      [
        cases,
        financialMovementForm.clientId,
      ]
    );

  const ledgerFilterCaseOptions =
    useMemo(
      () =>
        cases.filter(
          (caseItem) =>
            !ledgerClientFilter ||
            caseItem.clientId ===
              ledgerClientFilter
        ),
      [
        cases,
        ledgerClientFilter,
      ]
    );

  const filteredFinancialTransactions =
    useMemo(
      () => {
        const term =
          ledgerSearch
            .trim()
            .toLowerCase();

        return financialTransactions.filter(
          (transaction) => {
            if (
              ledgerCategoryFilter &&
              transaction.categoryId !==
                ledgerCategoryFilter
            ) {
              return false;
            }

            if (!term) {
              return true;
            }

            const haystack = [
              transaction.description,
              transaction.categoryName,
              transaction.clientName,
              transaction.caseTitle,
              transaction.reference,
              transaction.createdByName,
              ...transaction.entries.map(
                (entry) =>
                  entry.accountName
              ),
            ]
              .filter(Boolean)
              .join(' ')
              .toLowerCase();

            return haystack.includes(
              term
            );
          }
        );
      },
      [
        financialTransactions,
        ledgerCategoryFilter,
        ledgerSearch,
      ]
    );

  const ledgerIncomeCents =
    useMemo(
      () =>
        filteredFinancialTransactions
          .filter(
            (transaction) =>
              transaction.transactionType ===
                'income' &&
              !transaction.reversedById
          )
          .reduce(
            (sum, transaction) =>
              sum +
              transaction.amountCents,
            0
          ),
      [
        filteredFinancialTransactions,
      ]
    );

  const ledgerExpenseCents =
    useMemo(
      () =>
        filteredFinancialTransactions
          .filter(
            (transaction) =>
              transaction.transactionType ===
                'expense' &&
              !transaction.reversedById
          )
          .reduce(
            (sum, transaction) =>
              sum +
              transaction.amountCents,
            0
          ),
      [
        filteredFinancialTransactions,
      ]
    );

  const ledgerTransferCents =
    useMemo(
      () =>
        filteredFinancialTransactions
          .filter(
            (transaction) =>
              transaction.transactionType ===
                'transfer' &&
              !transaction.reversedById
          )
          .reduce(
            (sum, transaction) =>
              sum +
              transaction.amountCents,
            0
          ),
      [
        filteredFinancialTransactions,
      ]
    );

  const ledgerNetCents =
    ledgerIncomeCents -
    ledgerExpenseCents;

  useEffect(() => {
    void loadFinancialTransactions({
      type:
        ledgerTypeFilter ||
        undefined,

      accountId:
        ledgerAccountFilter ||
        undefined,

      clientId:
        ledgerClientFilter ||
        undefined,

      caseId:
        ledgerCaseFilter ||
        undefined,

      from:
        ledgerFromFilter ||
        undefined,

      to:
        ledgerToFilter ||
        undefined,

      limit: 200,
    });
  }, [
    loadFinancialTransactions,
    ledgerTypeFilter,
    ledgerAccountFilter,
    ledgerClientFilter,
    ledgerCaseFilter,
    ledgerFromFilter,
    ledgerToFilter,
  ]);

  useEffect(() => {
    void loadStatementEntries({
      accountId:
        reconciliationAccountFilter ||
        undefined,

      status:
        reconciliationStatusFilter ||
        undefined,

      from:
        reconciliationFromFilter ||
        undefined,

      to:
        reconciliationToFilter ||
        undefined,

      limit: 500,
    });
  }, [
    loadStatementEntries,
    reconciliationAccountFilter,
    reconciliationStatusFilter,
    reconciliationFromFilter,
    reconciliationToFilter,
  ]);

  const reconciliationPendingCount =
    useMemo(
      () =>
        statementEntries.filter(
          (entry) =>
            entry.status ===
            'pending'
        ).length,
      [statementEntries]
    );

  const reconciliationPartialCount =
    useMemo(
      () =>
        statementEntries.filter(
          (entry) =>
            entry.status ===
            'partially_reconciled'
        ).length,
      [statementEntries]
    );

  const reconciliationCompletedCount =
    useMemo(
      () =>
        statementEntries.filter(
          (entry) =>
            entry.status ===
            'reconciled'
        ).length,
      [statementEntries]
    );

  const reconciliationRemainingCents =
    useMemo(
      () =>
        statementEntries
          .filter(
            (entry) =>
              entry.status !==
                'reconciled' &&
              entry.status !==
                'ignored'
          )
          .reduce(
            (sum, entry) =>
              sum +
              entry.remainingCents,
            0
          ),
      [statementEntries]
    );

  const visibleReconciliationCandidates =
    useMemo(
      () => {
        if (
          reconciliationCandidateMode !==
            'manual' ||
          !reconciliationManualSearch.trim()
        ) {
          return reconciliationCandidates;
        }

        const term =
          reconciliationManualSearch
            .trim()
            .toLocaleLowerCase(
              'pt-BR'
            );

        return reconciliationCandidates.filter(
          (candidate) =>
            [
              candidate.description,
              candidate.reference,
              candidate.targetType,
            ]
              .filter(Boolean)
              .join(' ')
              .toLocaleLowerCase(
                'pt-BR'
              )
              .includes(term)
        );
      },
      [
        reconciliationCandidates,
        reconciliationCandidateMode,
        reconciliationManualSearch,
      ]
    );

  useEffect(() => {
    void loadFinancialMonthPeriods();
  }, [
    loadFinancialMonthPeriods,
  ]);

  useEffect(() => {
    if (
      financialClosingPeriod
    ) {
      void loadFinancialMonthDetail(
        financialClosingPeriod
      );
    }
  }, [
    financialClosingPeriod,
    loadFinancialMonthDetail,
  ]);

  const financialClosingTotalCents =
    useMemo(
      () =>
        financialMonthSnapshots.reduce(
          (sum, snapshot) =>
            sum +
            snapshot.closingBalanceCents,
          0
        ),
      [
        financialMonthSnapshots,
      ]
    );

  useEffect(() => {
    if (
      financialReportFrom &&
      financialReportTo
    ) {
      void loadFinancialReport({
        from:
          financialReportFrom,

        to:
          financialReportTo,

        accountId:
          financialReportAccountId ||
          undefined,
      });
    }
  }, [
    loadFinancialReport,
    financialReportFrom,
    financialReportTo,
    financialReportAccountId,
  ]);

  const filteredPayments = useMemo(() => {
    const term = search.trim().toLowerCase();

    return payments.filter((payment) => {
      if (!statusFilter && isArchivedOrCancelledPayment(payment)) {
        return false;
      }

      if (statusFilter && payment.status !== statusFilter) {
        return false;
      }

      if (
        chargeTypeFilter &&
        payment.chargeType !==
          chargeTypeFilter
      ) {
        return false;
      }

      if (term) {
        const haystack = [
          payment.clientName,
          payment.caseTitle,
          payment.feeRuleName,
          payment.title,

          financialChargeTypeLabel(
            payment.chargeType
          ),

          payment.paymentMethod,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        if (!haystack.includes(term)) {
          return false;
        }
      }

      return true;
    });
  }, [
    payments,
    search,
    statusFilter,
    chargeTypeFilter,
  ]);

  const displayPayments = useMemo(() => {
    const basePayments = [...filteredPayments];

    switch (viewMode) {
      case 'produto':
        return basePayments.sort(
          (left, right) => compareText(left.feeRuleName ?? 'Sem regra vinculada', right.feeRuleName ?? 'Sem regra vinculada') || compareText(left.title, right.title)
        );
      case 'caso':
        return basePayments.sort(
          (left, right) => compareText(left.caseTitle ?? 'Sem caso vinculado', right.caseTitle ?? 'Sem caso vinculado') || compareText(left.title, right.title)
        );
      case 'vencidas':
        return basePayments
          .filter((payment) => hasOverdueInstallment(payment))
          .sort((left, right) => compareDate(getPaymentPrimaryDueDate(left), getPaymentPrimaryDueDate(right)) || compareText(left.title, right.title));
      case 'avencer':
        return basePayments
          .filter((payment) => hasUnpaidInstallment(payment) && !hasOverdueInstallment(payment))
          .sort((left, right) => compareDate(getPaymentPrimaryDueDate(left), getPaymentPrimaryDueDate(right)) || compareText(left.title, right.title));
      case 'Vencidas':
        return basePayments
          .filter((payment) => payment.status === 'overdue' || hasOverdueInstallment(payment))
          .sort((left, right) => compareDate(getPaymentPrimaryDueDate(left), getPaymentPrimaryDueDate(right)) || compareText(left.title, right.title));
      default:
        return basePayments;
    }
  }, [filteredPayments, viewMode]);

  const operationalPayments = useMemo(() => payments.filter((payment) => !isArchivedOrCancelledPayment(payment)), [payments]);

  const totalContractedCents = useMemo(
    () =>
      operationalPayments
        .filter(
          (payment) =>
            payment.chargeType ===
            'contractual_fee'
        )
        .reduce(
          (sum, payment) =>
            sum +
            payment.totalAmountCents,
          0
        ),
    [operationalPayments]
  );

  const totalReceivedCents = useMemo(
    () =>
      operationalPayments.reduce(
        (sum, payment) => sum + (payment.paidAmountCents ?? 0),
        0
      ),
    [operationalPayments]
  );

  const totalPendingCents = useMemo(
    () =>
      operationalPayments.reduce(
        (sum, payment) => sum + (payment.balanceAmountCents ?? 0),
        0
      ),
    [operationalPayments]
  );

  const overdueAmountCents = useMemo(
    () =>
      operationalPayments
        .filter(
          (payment) =>
            payment.status === 'overdue' ||
            hasOverdueInstallment(payment)
        )
        .reduce(
          (sum, payment) =>
            sum + (payment.balanceAmountCents ?? 0),
          0
        ),
    [operationalPayments]
  );

  const overdueInstallmentsCount = useMemo(
    () => operationalPayments.flatMap((payment) => payment.installments).filter((installment) => installment.status === 'overdue').length,
    [operationalPayments]
  );

  const totalSuccessForecastCents = useMemo(
    () =>
      operationalPayments.reduce(
        (sum, payment) =>
          sum +
          Math.round(
            (payment.totalAmountCents *
              (payment.successFeePercentage ?? 0)) /
              100
          ),
        0
      ),
    [operationalPayments]
  );

  const paymentCountText = useMemo(
    () =>
      isLoadingPayments
        ? 'Carregando lançamentos financeiros...'
        : `${displayPayments.length} lançamentos · ${paymentsError ? 'Dados locais' : 'Controle financeiro'}`,
    [displayPayments.length, isLoadingPayments, paymentsError]
  );

  const isPaymentsActionBusy =
    isSavingPayment ||
    isSavingSettlement ||
    reversingSettlementId !== null ||
    paymentActionId !== null ||
    installmentActionId !== null;

  const installmentPreview = useMemo(() => {
    const totalAmountCents =
      resolvePaymentFormTotalCents(
        newCharge
      );

    const entryAmountCents =
      parseMoneyToCents(newCharge.entryAmount) ?? 0;

    const installmentCount =
      toNullableInteger(newCharge.installmentCount) ?? 0;

    if (
      totalAmountCents === null ||
      totalAmountCents <= 0 ||
      entryAmountCents < 0 ||
      entryAmountCents > totalAmountCents ||
      installmentCount < 1
    ) {
      return null;
    }

    const totalInstallmentCents =
      totalAmountCents - entryAmountCents;

    return {
      installments: splitMoneyCents(
        totalInstallmentCents,
        installmentCount
      ),
      totalInstallmentCents,
    };
  }, [
    newCharge,
  ]);

  const openNewChargeDrawer = () => {
    setPaymentBeingEdited(null);
    setNewCharge(buildEmptyPaymentForm());
    setShowNewCharge(true);
  };

  const openEditChargeDrawer = (payment: Payment) => {
    setPaymentBeingEdited(payment);
    setNewCharge(buildPaymentFormFromPayment(payment));
    setShowNewCharge(true);
  };

  const closeNewChargeDrawer = () => {
    if (isSavingPayment) {
      return;
    }

    setShowNewCharge(false);
    setPaymentBeingEdited(null);
    setNewCharge(buildEmptyPaymentForm());
  };

  const handleRetryPayments = () => {
    void loadPayments();
  };

  const handleSaveCharge = async () => {
    if (isSavingPayment) {
      return;
    }

    if (!newCharge.clientId) {
      addToast('Selecione um cliente antes de salvar.', 'warning');
      return;
    }

    if (!newCharge.title.trim()) {
      addToast('Preencha o título do lançamento financeiro.', 'warning');
      return;
    }

    const totalAmountCents =
      resolvePaymentFormTotalCents(
        newCharge
      );

    if (
      newCharge.chargeType ===
        'sucumbency' &&
      newCharge.sucumbencyCalculationType ===
        'percentage'
    ) {
      const basisCents =
        parseMoneyToCents(
          newCharge.sucumbencyBasisAmount
        );

      const percentage =
        toNullableNumber(
          newCharge.sucumbencyPercentage
        );

      if (
        basisCents === null ||
        basisCents <= 0
      ) {
        addToast(
          'Informe o valor-base da sucumbência.',
          'warning'
        );
        return;
      }

      if (
        percentage === null ||
        percentage <= 0 ||
        percentage > 100
      ) {
        addToast(
          'Informe um percentual válido para a sucumbência.',
          'warning'
        );
        return;
      }
    }

    const entryAmountCents = Math.max(
      0,
      parseMoneyToCents(newCharge.entryAmount) ?? 0
    );
    const installmentCount = toNullableInteger(newCharge.installmentCount) ?? 0;
    const isContractDateValid = !newCharge.contractDate || normalizeDateInput(newCharge.contractDate) !== null;
    const isFirstDueDateValid = !newCharge.firstDueDate || normalizeDateInput(newCharge.firstDueDate) !== null;

    if (totalAmountCents === null || totalAmountCents < 0) {
      addToast('Informe um valor total válido.', 'warning');
      return;
    }

    if (installmentCount < 1) {
      addToast('O número de parcelas deve ser de pelo menos 1.', 'warning');
      return;
    }

    if (entryAmountCents > totalAmountCents) {
      addToast('A entrada não pode ser maior que o valor total.', 'warning');
      return;
    }

    if (!isContractDateValid || !isFirstDueDateValid) {
      addToast('Informe datas válidas antes de salvar o lançamento financeiro.', 'warning');
      return;
    }

    if (!clientsLoading && clients.length === 0) {
      addToast('Cadastre um cliente antes de criar um lançamento financeiro.', 'warning');
      return;
    }

    setIsSavingPayment(true);

    try {
      const payload = buildCreatePaymentInput(newCharge);

      if (paymentBeingEdited) {
        await updatePayment(paymentBeingEdited.id, payload);
        addToast('Lançamento financeiro atualizado com sucesso.', 'success');
      } else {
        await createPayment(payload);
        addToast('Lançamento financeiro criado com sucesso.', 'success');
      }

      setShowNewCharge(false);
      setPaymentBeingEdited(null);
      setNewCharge(buildEmptyPaymentForm());
    } catch (error: any) {
      addToast(error?.message || 'Não foi possível salvar o lançamento financeiro.', 'error');
    } finally {
      setIsSavingPayment(false);
    }
  };

  const handleArchivePayment = async (payment: Payment) => {
    if (isPaymentsActionBusy || payment.status === 'archived') {
      return;
    }

    if (
      !window.confirm(
        'Tem certeza que deseja arquivar este lançamento financeiro? Esta ação remove o lançamento da listagem principal, mas mantém o registro no banco para auditoria.'
      )
    ) {
      return;
    }

    setPaymentActionId(payment.id);

    try {
      await archivePayment(payment.id);
      addToast('Lançamento financeiro arquivado com sucesso.', 'success');
      if (expandedId === payment.id) {
        setExpandedId(null);
      }
    } catch (error: any) {
      addToast(error?.message || 'Não foi possível arquivar o lançamento financeiro.', 'error');
    } finally {
      setPaymentActionId(null);
    }
  };

  const handleCancelPayment = async (payment: Payment) => {
    if (isPaymentsActionBusy || payment.status === 'cancelled') {
      return;
    }

    if (
      !window.confirm(
        'Tem certeza que deseja cancelar este lançamento financeiro? Esta ação remove o lançamento da listagem principal, mas mantém o registro no banco para auditoria.'
      )
    ) {
      return;
    }

    setPaymentActionId(payment.id);

    try {
      await updatePayment(payment.id, buildPaymentUpdateInputFromPayment(payment, { status: 'cancelled' }));
      addToast('Lançamento financeiro cancelado com sucesso.', 'success');
      if (expandedId === payment.id) {
        setExpandedId(null);
      }
    } catch (error: any) {
      addToast(error?.message || 'Não foi possível cancelar o lançamento financeiro.', 'error');
    } finally {
      setPaymentActionId(null);
    }
  };

  const openSettlementDrawer = (
    payment: Payment,
    installment?: PaymentInstallment
  ) => {
    if (
      payment.status === 'archived' ||
      payment.status === 'cancelled'
    ) {
      addToast(
        'Lançamentos arquivados ou cancelados não podem receber baixa.',
        'info'
      );
      return;
    }

    const paymentBalanceCents =
      Math.max(
        0,
        payment.balanceAmountCents ?? 0
      );

    const installmentBalanceCents =
      installment
        ? Math.max(
            0,
            installment.amountCents -
              (installment.paidAmountCents ?? 0)
          )
        : paymentBalanceCents;

    const defaultAmountCents =
      Math.min(
        paymentBalanceCents,
        installmentBalanceCents
      );

    if (defaultAmountCents <= 0) {
      addToast(
        'Não há saldo disponível para baixa.',
        'info'
      );
      return;
    }

    setSettlementPayment(payment);

    setSettlementForm({
      paymentId: payment.id,
      installmentId:
        installment?.id ?? '',
      amount:
        centsToDecimalString(
          defaultAmountCents
        ),
      occurredAt:
        todayInputDate(),
      paymentMethod:
        installment?.paymentMethod ??
        payment.paymentMethod ??
        'pix',
      financialAccountId:
        financialAccounts[0]?.id ??
        '',
      receiptReference: '',
      notes: '',
      idempotencyKey:
        crypto.randomUUID(),
    });

    setShowSettlementDrawer(true);
  };

  const closeSettlementDrawer = () => {
    if (isSavingSettlement) {
      return;
    }

    setShowSettlementDrawer(false);
    setSettlementPayment(null);
    setSettlementForm(
      buildEmptySettlementForm()
    );
  };

  const handleSaveSettlement = async () => {
    if (
      isSavingSettlement ||
      !settlementPayment
    ) {
      return;
    }

    const amountCents =
      parseMoneyToCents(
        settlementForm.amount
      );

    if (
      amountCents === null ||
      amountCents <= 0
    ) {
      addToast(
        'Informe um valor de baixa válido.',
        'warning'
      );
      return;
    }

    const paymentBalanceCents =
      Math.max(
        0,
        settlementPayment.balanceAmountCents ??
          0
      );

    if (
      amountCents >
      paymentBalanceCents
    ) {
      addToast(
        'A baixa não pode ultrapassar o saldo da cobrança.',
        'warning'
      );
      return;
    }

    if (
      !settlementForm.occurredAt
    ) {
      addToast(
        'Informe a data do recebimento.',
        'warning'
      );
      return;
    }

    if (
      !settlementForm.paymentMethod
    ) {
      addToast(
        'Informe a forma de pagamento.',
        'warning'
      );
      return;
    }

    if (
      !settlementForm.financialAccountId
    ) {
      addToast(
        'Selecione a conta financeira de destino.',
        'warning'
      );
      return;
    }

    const installment =
      settlementForm.installmentId
        ? settlementPayment.installments.find(
            (item) =>
              item.id ===
              settlementForm.installmentId
          )
        : null;

    if (
      settlementForm.installmentId &&
      !installment
    ) {
      addToast(
        'A parcela selecionada não foi encontrada.',
        'error'
      );
      return;
    }

    if (installment) {
      const installmentBalanceCents =
        Math.max(
          0,
          installment.amountCents -
            (installment.paidAmountCents ?? 0)
        );

      if (
        amountCents >
        installmentBalanceCents
      ) {
        addToast(
          'A baixa não pode ultrapassar o saldo da parcela.',
          'warning'
        );
        return;
      }
    }

    const occurredAt =
      new Date(
        `${settlementForm.occurredAt}T12:00:00`
      );

    if (
      Number.isNaN(
        occurredAt.getTime()
      )
    ) {
      addToast(
        'Informe uma data válida.',
        'warning'
      );
      return;
    }

    setIsSavingSettlement(true);

    if (installment) {
      setInstallmentActionId(
        installment.id
      );
    }

    try {
      const result =
        await createSettlement({
          paymentId:
            settlementPayment.id,

          amountCents,

          occurredAt:
            occurredAt.toISOString(),

          paymentMethod:
            settlementForm.paymentMethod,

          financialAccountId:
            settlementForm.financialAccountId,

          receiptReference:
            toNullableText(
              settlementForm.receiptReference
            ),

          notes:
            toNullableText(
              settlementForm.notes
            ),

          idempotencyKey:
            settlementForm.idempotencyKey,

          allocations:
            installment
              ? [
                  {
                    installmentId:
                      installment.id,
                    amountCents,
                  },
                ]
              : [],
        });

      await loadPayments();

      const refreshedSettlements =
        await loadSettlements(
          settlementPayment.id
        );

      setSettlementHistoryByPaymentId(
        (prev) => ({
          ...prev,
          [settlementPayment.id]:
            refreshedSettlements,
        })
      );

      addToast(
        result.duplicated
          ? 'Esta baixa já havia sido registrada.'
          : 'Baixa registrada com sucesso.',
        result.duplicated
          ? 'info'
          : 'success'
      );

      setShowSettlementDrawer(false);
      setSettlementPayment(null);
      setSettlementForm(
        buildEmptySettlementForm()
      );
    } catch (error: any) {
      addToast(
        error?.message ||
          'Não foi possível registrar a baixa.',
        'error'
      );
    } finally {
      setIsSavingSettlement(false);
      setInstallmentActionId(null);
    }
  };

  const handleRegisterPaymentShortcut = (
    payment: Payment
  ) => {
    if (
      isPaymentsActionBusy ||
      payment.status === 'archived' ||
      payment.status === 'cancelled'
    ) {
      addToast(
        'Lançamentos arquivados ou cancelados não podem receber pagamento.',
        'info'
      );
      return;
    }

    const installment =
      payment.installments.find(
        (item) =>
          item.status !== 'paid' &&
          item.status !== 'cancelled' &&
          item.amountCents -
            (item.paidAmountCents ?? 0) >
            0
      );

    if (installment) {
      openSettlementDrawer(
        payment,
        installment
      );
      return;
    }

    if (
      (payment.balanceAmountCents ??
        0) > 0
    ) {
      openSettlementDrawer(
        payment
      );
      return;
    }

    addToast(
      'Não há saldo disponível para pagamento.',
      'info'
    );
  };

  const loadPaymentSettlementHistory =
    async (
      paymentId: string
    ) => {
      setSettlementHistoryLoadingId(
        paymentId
      );

      try {
        const history =
          await loadSettlements(
            paymentId
          );

        setSettlementHistoryByPaymentId(
          (prev) => ({
            ...prev,
            [paymentId]:
              history,
          })
        );

        return history;
      } catch (error: any) {
        addToast(
          error?.message ||
            'Não foi possível carregar o histórico financeiro.',
          'error'
        );

        return [];
      } finally {
        setSettlementHistoryLoadingId(
          null
        );
      }
    };

  const handleTogglePayment = (
    payment: Payment
  ) => {
    if (
      expandedId ===
      payment.id
    ) {
      setExpandedId(null);
      return;
    }

    setExpandedId(
      payment.id
    );

    void loadPaymentSettlementHistory(
      payment.id
    );
  };

  const handleReverseSettlement =
    async (
      payment: Payment,
      settlement:
        PaymentSettlement
    ) => {
      if (
        reversingSettlementId
      ) {
        return;
      }

      const history =
        settlementHistoryByPaymentId[
          payment.id
        ] ?? [];

      const alreadyReversed =
        history.some(
          (item) =>
            item.kind ===
              'reversal' &&
            item.reversalOfId ===
              settlement.id
        );

      if (alreadyReversed) {
        addToast(
          'Esta baixa já foi estornada.',
          'info'
        );
        return;
      }

      if (
        settlement.kind !==
          'receipt' ||
        settlement.source ===
          'legacy_backfill' ||
        settlement.source ===
          'asaas'
      ) {
        addToast(
          'Este recebimento não pode ser estornado por esta operação.',
          'info'
        );
        return;
      }

      const reason =
        window.prompt(
          `Informe o motivo do estorno de ${formatCurrencyCents(settlement.amountCents)}:`
        );

      if (
        reason === null ||
        !reason.trim()
      ) {
        return;
      }

      if (
        !window.confirm(
          `Confirmar estorno de ${formatCurrencyCents(settlement.amountCents)}? A baixa original será preservada no histórico.`
        )
      ) {
        return;
      }

      setReversingSettlementId(
        settlement.id
      );

      try {
        await reverseSettlement(
          settlement.id,
          {
            reason:
              reason.trim(),
            occurredAt:
              new Date()
                .toISOString(),
          }
        );

        await loadPayments();

        await loadPaymentSettlementHistory(
          payment.id
        );

        addToast(
          'Baixa estornada com sucesso.',
          'success'
        );
      } catch (error: any) {
        addToast(
          error?.message ||
            'Não foi possível estornar a baixa.',
          'error'
        );
      } finally {
        setReversingSettlementId(
          null
        );
      }
    };

  const openNewFinancialAccountDrawer =
    () => {
      setFinancialAccountBeingEdited(
        null
      );

      setFinancialAccountForm(
        buildEmptyFinancialAccountForm()
      );

      setShowFinancialAccountDrawer(
        true
      );
    };

  const openEditFinancialAccountDrawer =
    (
      account: FinancialAccount
    ) => {
      setFinancialAccountBeingEdited(
        account
      );

      setFinancialAccountForm(
        buildFinancialAccountForm(
          account
        )
      );

      setShowFinancialAccountDrawer(
        true
      );
    };

  const closeFinancialAccountDrawer =
    () => {
      if (
        isSavingFinancialAccount
      ) {
        return;
      }

      setShowFinancialAccountDrawer(
        false
      );

      setFinancialAccountBeingEdited(
        null
      );

      setFinancialAccountForm(
        buildEmptyFinancialAccountForm()
      );
    };

  const handleSaveFinancialAccount =
    async () => {
      if (
        isSavingFinancialAccount
      ) {
        return;
      }

      if (
        !financialAccountForm.name.trim()
      ) {
        addToast(
          'Informe o nome da conta financeira.',
          'warning'
        );
        return;
      }

      const openingBalanceCents =
        parseMoneyToCents(
          financialAccountForm.openingBalance
        );

      if (
        openingBalanceCents === null
      ) {
        addToast(
          'Informe um saldo inicial válido.',
          'warning'
        );
        return;
      }

      setIsSavingFinancialAccount(
        true
      );

      try {
        const payload = {
          name:
            financialAccountForm.name.trim(),

          accountType:
            financialAccountForm.accountType,

          institution:
            toNullableText(
              financialAccountForm.institution
            ),

          openingBalanceCents,

          openingBalanceDate:
            toNullableText(
              financialAccountForm.openingBalanceDate
            ),

          notes:
            toNullableText(
              financialAccountForm.notes
            ),
        };

        if (
          financialAccountBeingEdited
        ) {
          await updateFinancialAccount(
            financialAccountBeingEdited.id,
            payload
          );

          addToast(
            'Conta financeira atualizada com sucesso.',
            'success'
          );
        } else {
          await createFinancialAccount(
            payload
          );

          addToast(
            'Conta financeira criada com sucesso.',
            'success'
          );
        }

        setShowFinancialAccountDrawer(
          false
        );

        setFinancialAccountBeingEdited(
          null
        );

        setFinancialAccountForm(
          buildEmptyFinancialAccountForm()
        );
      } catch (error: any) {
        addToast(
          error?.message ||
            'Não foi possível salvar a conta financeira.',
          'error'
        );
      } finally {
        setIsSavingFinancialAccount(
          false
        );
      }
    };

  const handleToggleFinancialAccount =
    async (
      account: FinancialAccount
    ) => {
      const action =
        account.isActive
          ? 'inativar'
          : 'reativar';

      if (
        !window.confirm(
          `Deseja ${action} a conta "${account.name}"?`
        )
      ) {
        return;
      }

      try {
        await updateFinancialAccount(
          account.id,
          {
            isActive:
              !account.isActive,
          }
        );

        addToast(
          account.isActive
            ? 'Conta financeira inativada.'
            : 'Conta financeira reativada.',
          'success'
        );
      } catch (error: any) {
        addToast(
          error?.message ||
            'Não foi possível alterar a conta financeira.',
          'error'
        );
      }
    };

  const openFinancialMovementDrawer =
    () => {
      const defaultType:
        FinancialMovementFormState['transactionType'] =
          'expense';

      const defaultAccount =
        financialAccounts[0];

      const defaultDestination =
        financialAccounts.find(
          (account) =>
            account.id !==
            defaultAccount?.id
        );

      const defaultCategory =
        financialCategories.find(
          (category) =>
            category.categoryType ===
              defaultType ||
            category.categoryType ===
              'both'
        );

      setFinancialMovementForm({
        ...buildEmptyFinancialMovementForm(),

        transactionType:
          defaultType,

        accountId:
          defaultAccount?.id ??
          '',

        destinationAccountId:
          defaultDestination?.id ??
          '',

        categoryId:
          defaultCategory?.id ??
          '',
      });

      setShowFinancialMovementDrawer(
        true
      );
    };

  const closeFinancialMovementDrawer =
    () => {
      if (
        isSavingFinancialMovement
      ) {
        return;
      }

      setShowFinancialMovementDrawer(
        false
      );

      setFinancialMovementForm(
        buildEmptyFinancialMovementForm()
      );
    };

  const handleSaveFinancialMovement =
    async () => {
      if (
        isSavingFinancialMovement
      ) {
        return;
      }

      const amountCents =
        parseMoneyToCents(
          financialMovementForm.amount
        );

      if (
        amountCents === null ||
        amountCents <= 0
      ) {
        addToast(
          'Informe um valor válido para a movimentação.',
          'warning'
        );
        return;
      }

      if (
        !financialMovementForm.accountId
      ) {
        addToast(
          'Selecione a conta financeira.',
          'warning'
        );
        return;
      }

      if (
        financialMovementForm.transactionType ===
          'transfer' &&
        (
          !financialMovementForm.destinationAccountId ||
          financialMovementForm.destinationAccountId ===
            financialMovementForm.accountId
        )
      ) {
        addToast(
          'Selecione uma conta de destino diferente da conta de origem.',
          'warning'
        );
        return;
      }

      if (
        financialMovementForm.transactionType !==
          'transfer' &&
        !financialMovementForm.categoryId
      ) {
        addToast(
          'Selecione a categoria financeira.',
          'warning'
        );
        return;
      }

      if (
        !financialMovementForm.description.trim()
      ) {
        addToast(
          'Informe a descrição da movimentação.',
          'warning'
        );
        return;
      }

      const occurredAt =
        new Date(
          `${financialMovementForm.occurredAt}T12:00:00`
        );

      if (
        Number.isNaN(
          occurredAt.getTime()
        )
      ) {
        addToast(
          'Informe uma data válida.',
          'warning'
        );
        return;
      }

      setIsSavingFinancialMovement(
        true
      );

      try {
        const result =
          await createFinancialTransaction({
            transactionType:
              financialMovementForm.transactionType,

            amountCents,

            accountId:
              financialMovementForm.accountId,

            destinationAccountId:
              financialMovementForm.transactionType ===
                'transfer'
                ? financialMovementForm.destinationAccountId
                : null,

            categoryId:
              financialMovementForm.transactionType ===
                'transfer'
                ? null
                : financialMovementForm.categoryId,

            clientId:
              toNullableText(
                financialMovementForm.clientId
              ),

            caseId:
              toNullableText(
                financialMovementForm.caseId
              ),

            occurredAt:
              occurredAt.toISOString(),

            description:
              financialMovementForm.description.trim(),

            reference:
              toNullableText(
                financialMovementForm.reference
              ),

            notes:
              toNullableText(
                financialMovementForm.notes
              ),

            idempotencyKey:
              financialMovementForm.idempotencyKey,
          });

        await Promise.all([
          loadFinancialTransactions({
            type:
              ledgerTypeFilter ||
              undefined,

            accountId:
              ledgerAccountFilter ||
              undefined,

            clientId:
              ledgerClientFilter ||
              undefined,

            caseId:
              ledgerCaseFilter ||
              undefined,

            from:
              ledgerFromFilter ||
              undefined,

            to:
              ledgerToFilter ||
              undefined,

            limit: 200,
          }),

          loadFinancialAccounts(),
          loadAllFinancialAccounts(),
        ]);

        addToast(
          result.duplicated
            ? 'Esta movimentação já havia sido registrada.'
            : 'Movimentação registrada com sucesso.',
          result.duplicated
            ? 'info'
            : 'success'
        );

        setShowFinancialMovementDrawer(
          false
        );

        setFinancialMovementForm(
          buildEmptyFinancialMovementForm()
        );
      } catch (error: any) {
        addToast(
          error?.message ||
            'Não foi possível registrar a movimentação.',
          'error'
        );
      } finally {
        setIsSavingFinancialMovement(
          false
        );
      }
    };

  const handleReverseFinancialTransaction =
    async (
      transaction:
        FinancialTransaction
    ) => {
      if (
        reversingFinancialTransactionId
      ) {
        return;
      }

      if (
        transaction.transactionType ===
          'reversal' ||
        transaction.reversedById
      ) {
        addToast(
          'Esta movimentação já está estornada.',
          'info'
        );
        return;
      }

      if (
        authUser?.role ===
        'secretary'
      ) {
        addToast(
          'O estorno exige perfil de advogado ou administrador.',
          'warning'
        );
        return;
      }

      if (
        transaction.source !==
        'manual'
      ) {
        addToast(
          'Esta movimentação não pode ser estornada por esta operação.',
          'info'
        );
        return;
      }

      const reason =
        window.prompt(
          `Informe o motivo do estorno de ${formatCurrencyCents(transaction.amountCents)}:`
        );

      if (
        reason === null ||
        !reason.trim()
      ) {
        return;
      }

      if (
        !window.confirm(
          `Confirmar estorno de ${formatCurrencyCents(transaction.amountCents)}? A movimentação original será preservada no histórico.`
        )
      ) {
        return;
      }

      setReversingFinancialTransactionId(
        transaction.id
      );

      try {
        await reverseFinancialTransaction(
          transaction.id,
          {
            reason:
              reason.trim(),

            occurredAt:
              new Date()
                .toISOString(),
          }
        );

        await Promise.all([
          loadFinancialTransactions({
            type:
              ledgerTypeFilter ||
              undefined,

            accountId:
              ledgerAccountFilter ||
              undefined,

            clientId:
              ledgerClientFilter ||
              undefined,

            caseId:
              ledgerCaseFilter ||
              undefined,

            from:
              ledgerFromFilter ||
              undefined,

            to:
              ledgerToFilter ||
              undefined,

            limit: 200,
          }),

          loadFinancialAccounts(),
          loadAllFinancialAccounts(),
        ]);

        addToast(
          'Movimentação estornada com sucesso.',
          'success'
        );
      } catch (error: any) {
        addToast(
          error?.message ||
            'Não foi possível estornar a movimentação.',
          'error'
        );
      } finally {
        setReversingFinancialTransactionId(
          null
        );
      }
    };

  const getTransactionAccountsLabel =
    (
      transaction:
        FinancialTransaction
    ) => {
      if (
        transaction.transactionType ===
        'transfer'
      ) {
        const source =
          transaction.entries.find(
            (entry) =>
              entry.entryRole ===
              'transfer_source'
          );

        const destination =
          transaction.entries.find(
            (entry) =>
              entry.entryRole ===
              'transfer_destination'
          );

        return `${
          source?.accountName ??
          'Conta'
        } → ${
          destination?.accountName ??
          'Conta'
        }`;
      }

      return (
        transaction.entries[0]
          ?.accountName ??
        '—'
      );
    };

  const openStatementEntryDrawer =
    () => {
      setStatementDirection(
        'debit'
      );

      setStatementAccountId(
        financialAccounts[0]?.id ??
          ''
      );

      setStatementPostedAt(
        todayInputDate()
      );

      setStatementAmount('');
      setStatementDescription('');
      setStatementReference('');
      setStatementBalanceAfter('');

      setShowStatementEntryDrawer(
        true
      );
    };

  const closeStatementEntryDrawer =
    () => {
      if (
        isSavingStatementEntry
      ) {
        return;
      }

      setShowStatementEntryDrawer(
        false
      );
    };

  const handleSaveStatementEntry =
    async () => {
      if (
        isSavingStatementEntry
      ) {
        return;
      }

      if (!statementAccountId) {
        addToast(
          'Selecione a conta financeira.',
          'warning'
        );
        return;
      }

      const parsedAmount =
        parseMoneyToCents(
          statementAmount
        );

      if (
        parsedAmount === null ||
        parsedAmount === 0
      ) {
        addToast(
          'Informe um valor bancário válido.',
          'warning'
        );
        return;
      }

      if (
        !statementDescription.trim()
      ) {
        addToast(
          'Informe a descrição do lançamento bancário.',
          'warning'
        );
        return;
      }

      const date =
        new Date(
          `${statementPostedAt}T12:00:00`
        );

      if (
        Number.isNaN(
          date.getTime()
        )
      ) {
        addToast(
          'Informe uma data válida.',
          'warning'
        );
        return;
      }

      const absoluteAmount =
        Math.abs(
          parsedAmount
        );

      const signedAmount =
        statementDirection ===
          'credit'
          ? absoluteAmount
          : -absoluteAmount;

      const balanceAfterCents =
        statementBalanceAfter.trim()
          ? parseMoneyToCents(
              statementBalanceAfter
            )
          : null;

      if (
        statementBalanceAfter.trim() &&
        balanceAfterCents === null
      ) {
        addToast(
          'Informe um saldo posterior válido.',
          'warning'
        );
        return;
      }

      setIsSavingStatementEntry(
        true
      );

      try {
        const result =
          await importStatementEntries({
            accountId:
              statementAccountId,

            sourceType:
              'manual',

            entries: [
              {
                postedAt:
                  date.toISOString(),

                amountCents:
                  signedAmount,

                balanceAfterCents,

                description:
                  statementDescription.trim(),

                reference:
                  toNullableText(
                    statementReference
                  ),
              },
            ],
          });

        await loadStatementEntries({
          accountId:
            reconciliationAccountFilter ||
            undefined,

          status:
            reconciliationStatusFilter ||
            undefined,

          from:
            reconciliationFromFilter ||
            undefined,

          to:
            reconciliationToFilter ||
            undefined,

          limit: 500,
        });

        addToast(
          result.duplicateCount > 0
            ? 'O lançamento já existia no extrato.'
            : 'Lançamento bancário incluído com sucesso.',
          result.duplicateCount > 0
            ? 'info'
            : 'success'
        );

        setShowStatementEntryDrawer(
          false
        );
      } catch (error: any) {
        addToast(
          error?.message ||
            'Não foi possível incluir o lançamento bancário.',
          'error'
        );
      } finally {
        setIsSavingStatementEntry(
          false
        );
      }
    };

  const openReconciliationDrawer =
    async (
      entry:
        FinancialStatementEntry
    ) => {
      if (
        entry.status ===
        'reconciled'
      ) {
        addToast(
          'Este lançamento já está totalmente conciliado.',
          'info'
        );
        return;
      }

      if (
        entry.status ===
        'ignored'
      ) {
        addToast(
          'Lançamentos ignorados não podem ser conciliados.',
          'info'
        );
        return;
      }

      setSelectedStatementEntry(
        entry
      );

      setReconciliationCandidates(
        []
      );

      setReconciliationCandidateMode(
        'automatic'
      );

      setReconciliationManualSearch(
        ''
      );

      setShowReconciliationDrawer(
        true
      );

      setIsLoadingCandidates(
        true
      );

      try {
        const candidates =
          await loadCandidates(
            entry.id,
            'automatic'
          );

        setReconciliationCandidates(
          candidates
        );
      } catch (error: any) {
        addToast(
          error?.message ||
            'Não foi possível localizar candidatos.',
          'error'
        );
      } finally {
        setIsLoadingCandidates(
          false
        );
      }
    };

  const closeReconciliationDrawer =
    () => {
      if (
        matchingCandidateId
      ) {
        return;
      }

      setShowReconciliationDrawer(
        false
      );

      setSelectedStatementEntry(
        null
      );

      setReconciliationCandidates(
        []
      );
    };

  const handleLoadManualCandidates =
    async () => {
      if (
        !selectedStatementEntry ||
        isLoadingCandidates ||
        matchingCandidateId
      ) {
        return;
      }

      setReconciliationCandidateMode(
        'manual'
      );

      setReconciliationManualSearch(
        ''
      );

      setIsLoadingCandidates(
        true
      );

      try {
        const candidates =
          await loadCandidates(
            selectedStatementEntry.id,
            'manual'
          );

        setReconciliationCandidates(
          candidates
        );

        if (
          candidates.length === 0
        ) {
          addToast(
            'Nenhuma movimentação interna disponível nesta conta e direção financeira.',
            'info'
          );
        }
      } catch (error: any) {
        addToast(
          error?.message ||
            'Não foi possível carregar a conciliação manual.',
          'error'
        );
      } finally {
        setIsLoadingCandidates(
          false
        );
      }
    };

  const handleLoadAutomaticCandidates =
    async () => {
      if (
        !selectedStatementEntry ||
        isLoadingCandidates ||
        matchingCandidateId
      ) {
        return;
      }

      setReconciliationCandidateMode(
        'automatic'
      );

      setReconciliationManualSearch(
        ''
      );

      setIsLoadingCandidates(
        true
      );

      try {
        const candidates =
          await loadCandidates(
            selectedStatementEntry.id,
            'automatic'
          );

        setReconciliationCandidates(
          candidates
        );
      } catch (error: any) {
        addToast(
          error?.message ||
            'Não foi possível carregar as sugestões automáticas.',
          'error'
        );
      } finally {
        setIsLoadingCandidates(
          false
        );
      }
    };

  const handleReconcileCandidate =
    async (
      candidate:
        ReconciliationCandidate
    ) => {
      if (
        !selectedStatementEntry ||
        matchingCandidateId
      ) {
        return;
      }

      const maximumMatchCents =
        Math.min(
          selectedStatementEntry
            .remainingCents,
          candidate.remainingCents
        );

      if (
        maximumMatchCents <= 0
      ) {
        addToast(
          'Não há saldo disponível para conciliação.',
          'info'
        );
        return;
      }

      let matchAmountCents =
        maximumMatchCents;

      if (
        reconciliationCandidateMode ===
        'manual'
      ) {
        const informed =
          window.prompt(
            'Informe o valor que deseja conciliar:',
            centsToDecimalString(
              maximumMatchCents
            )
          );

        if (
          informed === null
        ) {
          return;
        }

        const parsed =
          parseMoneyToCents(
            informed
          );

        if (
          parsed === null ||
          parsed <= 0 ||
          parsed >
            maximumMatchCents
        ) {
          addToast(
            `O valor deve ser maior que zero e não pode ultrapassar ${formatCurrencyCents(maximumMatchCents)}.`,
            'warning'
          );
          return;
        }

        matchAmountCents =
          parsed;
      }

      if (
        !window.confirm(
          `Conciliar ${formatCurrencyCents(matchAmountCents)} com "${candidate.description}"?`
        )
      ) {
        return;
      }

      setMatchingCandidateId(
        candidate.targetId
      );

      try {
        const result =
          await createReconciliationMatch({
            statementEntryId:
              selectedStatementEntry.id,

            targetType:
              candidate.targetType,

            targetId:
              candidate.targetId,

            matchedAmountCents:
              matchAmountCents,

            matchMethod:
              reconciliationCandidateMode ===
                'manual'
                ? 'manual'
                : 'automatic',

            confidence:
              reconciliationCandidateMode ===
                'manual'
                ? null
                : candidate.confidence,
          });

        const entries =
          await loadStatementEntries({
            accountId:
              reconciliationAccountFilter ||
              undefined,

            status:
              reconciliationStatusFilter ||
              undefined,

            from:
              reconciliationFromFilter ||
              undefined,

            to:
              reconciliationToFilter ||
              undefined,

            limit: 500,
          });

        const refreshed =
          entries.find(
            (entry) =>
              entry.id ===
              selectedStatementEntry.id
          );

        addToast(
          result.statement.status ===
            'reconciled'
            ? 'Lançamento conciliado com sucesso.'
            : 'Conciliação parcial registrada.',
          'success'
        );

        if (
          !refreshed ||
          refreshed.status ===
            'reconciled'
        ) {
          setShowReconciliationDrawer(
            false
          );

          setSelectedStatementEntry(
            null
          );

          setReconciliationCandidates(
            []
          );
        } else {
          setSelectedStatementEntry(
            refreshed
          );

          const candidates =
            await loadCandidates(
              refreshed.id,
              reconciliationCandidateMode
            );

          setReconciliationCandidates(
            candidates
          );
        }
      } catch (error: any) {
        addToast(
          error?.message ||
            'Não foi possível concluir a conciliação.',
          'error'
        );
      } finally {
        setMatchingCandidateId(
          null
        );
      }
    };
  const reconciliationStatusLabel =
    (status: string) => {
      switch (status) {
        case 'pending':
          return 'Pendente';

        case 'partially_reconciled':
          return 'Parcial';

        case 'reconciled':
          return 'Conciliado';

        case 'ignored':
          return 'Ignorado';

        default:
          return status;
      }
    };

  const handleCloseFinancialMonth =
    async () => {
      if (
        !financialClosingPeriod ||
        isProcessingFinancialClosing
      ) {
        return;
      }

      if (
        selectedFinancialMonth?.status ===
        'closed'
      ) {
        addToast(
          'Esta competência já está fechada.',
          'info'
        );
        return;
      }

      const reason =
        window.prompt(
          `Fechar a competência ${financialClosingPeriod}?\n\nObservação do fechamento (opcional):`,
          ''
        );

      if (reason === null) {
        return;
      }

      if (
        !window.confirm(
          `Confirmar o fechamento de ${financialClosingPeriod}? Novos lançamentos retroativos nesta competência ficarão bloqueados.`
        )
      ) {
        return;
      }

      setIsProcessingFinancialClosing(
        true
      );

      try {
        await closeFinancialMonth(
          financialClosingPeriod,
          reason.trim() || null
        );

        await Promise.all([
          loadFinancialMonthPeriods(),

          loadFinancialMonthDetail(
            financialClosingPeriod
          ),
        ]);

        addToast(
          'Competência financeira fechada com sucesso.',
          'success'
        );
      } catch (error: any) {
        const unresolvedCount =
          Number(
            error?.details
              ?.unresolvedCount ??
              0
          );

        const unresolvedAmountCents =
          Number(
            error?.details
              ?.unresolvedAmountCents ??
              0
          );

        if (
          unresolvedCount > 0
        ) {
          addToast(
            `${unresolvedCount} lançamento(s) bancário(s) ainda precisam ser conciliados, totalizando ${formatCurrencyCents(unresolvedAmountCents)}.`,
            'warning'
          );
        } else {
          addToast(
            error?.message ||
              'Não foi possível fechar a competência.',
            'error'
          );
        }
      } finally {
        setIsProcessingFinancialClosing(
          false
        );
      }
    };

  const handleReopenFinancialMonth =
    async () => {
      if (
        !financialClosingPeriod ||
        isProcessingFinancialClosing
      ) {
        return;
      }

      if (
        authUser?.role !==
        'admin'
      ) {
        addToast(
          'Somente o Administrador pode reabrir uma competência.',
          'warning'
        );
        return;
      }

      const reason =
        window.prompt(
          `Informe obrigatoriamente o motivo da reabertura de ${financialClosingPeriod}:`
        );

      if (
        reason === null ||
        !reason.trim()
      ) {
        return;
      }

      if (
        !window.confirm(
          `Confirmar a reabertura da competência ${financialClosingPeriod}?`
        )
      ) {
        return;
      }

      setIsProcessingFinancialClosing(
        true
      );

      try {
        await reopenFinancialMonth(
          financialClosingPeriod,
          reason.trim()
        );

        await Promise.all([
          loadFinancialMonthPeriods(),

          loadFinancialMonthDetail(
            financialClosingPeriod
          ),
        ]);

        addToast(
          'Competência financeira reaberta com sucesso.',
          'success'
        );
      } catch (error: any) {
        addToast(
          error?.message ||
            'Não foi possível reabrir a competência.',
          'error'
        );
      } finally {
        setIsProcessingFinancialClosing(
          false
        );
      }
    };

  const handleExportPayments = () => {
    const exported = displayPayments.map((payment) => ({
      id: payment.id,
      title: payment.title,
      clientName: payment.clientName ?? '',
      caseTitle: payment.caseTitle ?? '',
      status: payment.status,
      totalAmountCents: payment.totalAmountCents,
      totalAmount: payment.totalAmount,
      paidAmountCents: payment.paidAmountCents,
      paidAmount: payment.paidAmount,
      balanceAmountCents: payment.balanceAmountCents,
      balanceAmount: payment.balanceAmount,
      installmentCount: payment.installmentCount,
      firstDueDate: payment.firstDueDate,
      installments: payment.installments.map((installment) => ({
        id: installment.id,
        dueDate: installment.dueDate,
        amountCents: installment.amountCents,
        amount: installment.amount,
        paidAmountCents: installment.paidAmountCents,
        paidAmount: installment.paidAmount,
        status: installment.status,
      })),
    }));

    downloadJsonFile(`financeiro-${new Date().toISOString().slice(0, 10)}.json`, {
      exportedAt: new Date().toISOString(),
      count: exported.length,
      data: exported,
    });
  };

  const views: { key: ViewMode; label: string }[] = [
    { key: 'lista', label: 'Lista Geral' },
    { key: 'produto', label: 'Por Produto' },
    { key: 'caso', label: 'Por Caso' },
    { key: 'vencidas', label: 'Vencidas' },
    { key: 'avencer', label: 'A Vencer' },
  ];

  return (
    <AppLayout>
      <div className="p-4 md:p-6 space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold text-foreground-900">Financeiro</h2>
            <p className="text-sm text-foreground-500 mt-0.5">{paymentCountText}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={openNewChargeDrawer}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-primary-500 text-background-50 rounded-lg text-xs font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line"></i> Nova Cobrança
            </button>
            <button
              onClick={handleExportPayments}
              className="inline-flex items-center gap-1.5 px-3 py-2 border border-background-200 rounded-lg text-xs font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-download-line"></i> Exportar
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
          <div className="bg-background-50 border border-background-200 rounded-lg p-3">
            <p className="text-[10px] text-foreground-400 uppercase">Receita Contratada</p>
            <p className="text-base font-bold text-foreground-900 mt-1">{formatCurrencyCents(totalContractedCents)}</p>
          </div>
          <div className="bg-background-50 border border-background-200 rounded-lg p-3">
            <p className="text-[10px] text-foreground-400 uppercase">Recebido</p>
            <p className="text-base font-bold text-emerald-600 mt-1">{formatCurrencyCents(totalReceivedCents)}</p>
          </div>
          <div className="bg-background-50 border border-background-200 rounded-lg p-3">
            <p className="text-[10px] text-foreground-400 uppercase">A Receber</p>
            <p className="text-base font-bold text-amber-600 mt-1">{formatCurrencyCents(totalPendingCents)}</p>
          </div>
          <div className="bg-background-50 border border-background-200 rounded-lg p-3">
            <p className="text-[10px] text-foreground-400 uppercase">Vencidas</p>
            <p className="text-base font-bold text-red-500 mt-1">{formatCurrencyCents(overdueAmountCents)}</p>
          </div>
          <div className="bg-background-50 border border-background-200 rounded-lg p-3">
            <p className="text-[10px] text-foreground-400 uppercase">Vencidas</p>
            <p className="text-base font-bold text-red-500 mt-1">{overdueInstallmentsCount}</p>
          </div>
          <div className="bg-background-50 border border-background-200 rounded-lg p-3">
            <p className="text-[10px] text-foreground-400 uppercase">Êxito previsto</p>
            <p className="text-base font-bold text-primary-700 mt-1">{formatCurrencyCents(totalSuccessForecastCents)}</p>
          </div>
        </div>

        <div className="bg-background-50 border border-background-200 rounded-xl p-4">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div>
              <h3 className="text-sm font-semibold text-foreground-800">
                Contas Financeiras
              </h3>

              <p className="text-xs text-foreground-500 mt-0.5">
                Saldos calculados pelos lançamentos financeiros registrados no sistema.
              </p>
            </div>

            <button
              onClick={openNewFinancialAccountDrawer}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-50 text-primary-700 rounded-lg text-xs font-medium hover:bg-primary-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-bank-card-line"></i>
              Nova conta
            </button>
          </div>

          {financialAccountsManagementError && (
            <div className="mb-3 p-3 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700">
              {financialAccountsManagementError}
            </div>
          )}

          {isLoadingAllFinancialAccounts ? (
            <p className="text-xs text-foreground-500 py-3">
              Carregando contas financeiras...
            </p>
          ) : allFinancialAccounts.length === 0 ? (
            <p className="text-xs text-foreground-500 py-3">
              Nenhuma conta financeira cadastrada.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {allFinancialAccounts.map((account) => (
                <div
                  key={account.id}
                  className={`border rounded-xl p-3 ${
                    account.isActive
                      ? 'border-background-200 bg-background-50'
                      : 'border-background-200 bg-background-100 opacity-70'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold text-foreground-800">
                          {account.name}
                        </p>

                        <span
                          className={`text-[9px] px-1.5 py-0.5 rounded-full ${
                            account.isActive
                              ? 'bg-emerald-50 text-emerald-700'
                              : 'bg-background-200 text-foreground-500'
                          }`}
                        >
                          {account.isActive
                            ? 'Ativa'
                            : 'Inativa'}
                        </span>
                      </div>

                      <p className="text-[10px] text-foreground-400 mt-0.5">
                        {financialAccountTypeLabel(account.accountType)}
                        {account.institution
                          ? ` · ${account.institution}`
                          : ''}
                      </p>
                    </div>

                    <i className="ri-bank-line text-lg text-foreground-300"></i>
                  </div>

                  <div className="mt-3">
                    <p className="text-[10px] text-foreground-400 uppercase">
                      Saldo atual
                    </p>

                    <p
                      className={`text-lg font-bold mt-0.5 ${
                        account.currentBalanceCents >= 0
                          ? 'text-emerald-700'
                          : 'text-red-600'
                      }`}
                    >
                      {formatCurrencyCents(account.currentBalanceCents)}
                    </p>
                  </div>

                  <div className="grid grid-cols-4 gap-2 mt-3 text-[10px]">
                    <div>
                      <p className="text-foreground-400">
                        Cobranças
                      </p>

                      <p className="font-semibold text-emerald-600">
                        {formatCurrencyCents(account.receiptCents)}
                      </p>
                    </div>

                    <div>
                      <p className="text-foreground-400">
                        Estornos
                      </p>

                      <p className="font-semibold text-red-500">
                        {formatCurrencyCents(account.reversalCents)}
                      </p>
                    </div>

                    <div>
                      <p className="text-foreground-400">
                        Livro Caixa
                      </p>

                      <p
                        className={`font-semibold ${
                          account.ledgerCents >= 0
                            ? 'text-emerald-600'
                            : 'text-red-500'
                        }`}
                      >
                        {formatCurrencyCents(account.ledgerCents)}
                      </p>
                    </div>

                    <div>
                      <p className="text-foreground-400">
                        Movimentos
                      </p>

                      <p className="font-semibold text-foreground-700">
                        {account.movementCount}
                      </p>
                    </div>
                  </div>

                  <div className="mt-3 pt-2 border-t border-background-200 text-[10px] text-foreground-500">
                    Saldo inicial:{' '}
                    <span className="font-medium text-foreground-700">
                      {formatCurrencyCents(account.openingBalanceCents)}
                    </span>
                    {account.openingBalanceDate && (
                      <>
                        {' · '}
                        {formatDate(account.openingBalanceDate)}
                      </>
                    )}
                  </div>

                  <div className="flex items-center gap-2 mt-3">
                    <button
                      onClick={() =>
                        openEditFinancialAccountDrawer(account)
                      }
                      className="text-[10px] px-2 py-1 rounded bg-background-100 text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer"
                    >
                      <i className="ri-edit-line mr-1"></i>
                      Editar
                    </button>

                    <button
                      onClick={() =>
                        void handleToggleFinancialAccount(account)
                      }
                      className={`text-[10px] px-2 py-1 rounded transition-colors cursor-pointer ${
                        account.isActive
                          ? 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                          : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                      }`}
                    >
                      <i
                        className={`${
                          account.isActive
                            ? 'ri-pause-circle-line'
                            : 'ri-play-circle-line'
                        } mr-1`}
                      ></i>

                      {account.isActive
                        ? 'Inativar'
                        : 'Reativar'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-background-50 border border-background-200 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-background-200">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-foreground-800">
                  Movimentações / Livro Caixa
                </h3>

                <p className="text-xs text-foreground-500 mt-0.5">
                  Receitas, despesas e transferências efetivamente realizadas.
                </p>
              </div>

              <button
                onClick={openFinancialMovementDrawer}
                disabled={
                  financialAccounts.length === 0 ||
                  isLoadingFinancialCategories
                }
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-primary-500 text-white rounded-lg text-xs font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <i className="ri-add-line"></i>
                Nova movimentação
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 p-4 border-b border-background-200 bg-background-100/30">
            <div>
              <p className="text-[10px] uppercase text-foreground-400">
                Receitas
              </p>

              <p className="text-sm font-bold text-emerald-600 mt-1">
                {formatCurrencyCents(ledgerIncomeCents)}
              </p>
            </div>

            <div>
              <p className="text-[10px] uppercase text-foreground-400">
                Despesas
              </p>

              <p className="text-sm font-bold text-red-500 mt-1">
                {formatCurrencyCents(ledgerExpenseCents)}
              </p>
            </div>

            <div>
              <p className="text-[10px] uppercase text-foreground-400">
                Resultado
              </p>

              <p
                className={`text-sm font-bold mt-1 ${
                  ledgerNetCents >= 0
                    ? 'text-emerald-700'
                    : 'text-red-600'
                }`}
              >
                {formatCurrencyCents(ledgerNetCents)}
              </p>
            </div>

            <div>
              <p className="text-[10px] uppercase text-foreground-400">
                Transferências
              </p>

              <p className="text-sm font-bold text-primary-700 mt-1">
                {formatCurrencyCents(ledgerTransferCents)}
              </p>
            </div>
          </div>

          <div className="p-4 border-b border-background-200 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
              <input
                type="text"
                value={ledgerSearch}
                onChange={(e) =>
                  setLedgerSearch(
                    e.target.value
                  )
                }
                placeholder="Buscar descrição, cliente, caso..."
                className="px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-xs"
              />

              <select
                value={ledgerTypeFilter}
                onChange={(e) =>
                  setLedgerTypeFilter(
                    e.target.value
                  )
                }
                className="px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-xs cursor-pointer"
              >
                <option value="">
                  Todos os tipos
                </option>

                {financialTransactionTypeOptions.map(
                  (option) => (
                    <option
                      key={option.value}
                      value={option.value}
                    >
                      {option.label}
                    </option>
                  )
                )}
              </select>

              <select
                value={ledgerAccountFilter}
                onChange={(e) =>
                  setLedgerAccountFilter(
                    e.target.value
                  )
                }
                className="px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-xs cursor-pointer"
              >
                <option value="">
                  Todas as contas
                </option>

                {allFinancialAccounts.map(
                  (account) => (
                    <option
                      key={account.id}
                      value={account.id}
                    >
                      {account.name}
                    </option>
                  )
                )}
              </select>

              <select
                value={ledgerCategoryFilter}
                onChange={(e) =>
                  setLedgerCategoryFilter(
                    e.target.value
                  )
                }
                className="px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-xs cursor-pointer"
              >
                <option value="">
                  Todas as categorias
                </option>

                {financialCategories.map(
                  (category) => (
                    <option
                      key={category.id}
                      value={category.id}
                    >
                      {category.name}
                    </option>
                  )
                )}
              </select>

              <select
                value={ledgerClientFilter}
                onChange={(e) => {
                  setLedgerClientFilter(
                    e.target.value
                  );

                  setLedgerCaseFilter(
                    ''
                  );
                }}
                className="px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-xs cursor-pointer"
              >
                <option value="">
                  Todos os clientes
                </option>

                {clients.map(
                  (client) => (
                    <option
                      key={client.id}
                      value={client.id}
                    >
                      {client.name}
                    </option>
                  )
                )}
              </select>

              <select
                value={ledgerCaseFilter}
                onChange={(e) =>
                  setLedgerCaseFilter(
                    e.target.value
                  )
                }
                className="px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-xs cursor-pointer"
              >
                <option value="">
                  Todos os casos
                </option>

                {ledgerFilterCaseOptions.map(
                  (caseItem) => (
                    <option
                      key={caseItem.id}
                      value={caseItem.id}
                    >
                      {caseItem.title}
                    </option>
                  )
                )}
              </select>

              <input
                type="date"
                value={ledgerFromFilter}
                onChange={(e) =>
                  setLedgerFromFilter(
                    e.target.value
                  )
                }
                className="px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-xs"
                title="Data inicial"
              />

              <input
                type="date"
                value={ledgerToFilter}
                onChange={(e) =>
                  setLedgerToFilter(
                    e.target.value
                  )
                }
                className="px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-xs"
                title="Data final"
              />
            </div>
          </div>

          {financialLedgerError && (
            <div className="m-4 p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
              {financialLedgerError}
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-background-100/50 border-b border-background-200">
                  <th className="text-left px-4 py-3 text-[10px] uppercase text-foreground-500">
                    Data
                  </th>

                  <th className="text-left px-4 py-3 text-[10px] uppercase text-foreground-500">
                    Tipo
                  </th>

                  <th className="text-left px-4 py-3 text-[10px] uppercase text-foreground-500">
                    Descrição
                  </th>

                  <th className="text-left px-4 py-3 text-[10px] uppercase text-foreground-500">
                    Conta
                  </th>

                  <th className="text-left px-4 py-3 text-[10px] uppercase text-foreground-500">
                    Categoria / Vínculo
                  </th>

                  <th className="text-right px-4 py-3 text-[10px] uppercase text-foreground-500">
                    Valor
                  </th>

                  <th className="text-right px-4 py-3 text-[10px] uppercase text-foreground-500">
                    Ações
                  </th>
                </tr>
              </thead>

              <tbody>
                {isLoadingFinancialTransactions ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-4 py-8 text-center text-xs text-foreground-500"
                    >
                      Carregando movimentações...
                    </td>
                  </tr>
                ) : filteredFinancialTransactions.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-4 py-8 text-center text-xs text-foreground-500"
                    >
                      Nenhuma movimentação encontrada.
                    </td>
                  </tr>
                ) : (
                  filteredFinancialTransactions.map(
                    (transaction) => (
                      <tr
                        key={transaction.id}
                        className="border-b border-background-100 hover:bg-background-50"
                      >
                        <td className="px-4 py-3 text-xs text-foreground-600 whitespace-nowrap">
                          {formatDate(transaction.occurredAt)}
                        </td>

                        <td className="px-4 py-3">
                          <span
                            className={`text-[10px] font-medium px-2 py-1 rounded-full ${
                              transaction.transactionType === 'income'
                                ? 'bg-emerald-50 text-emerald-700'
                                : transaction.transactionType === 'expense'
                                  ? 'bg-red-50 text-red-700'
                                  : transaction.transactionType === 'transfer'
                                    ? 'bg-blue-50 text-blue-700'
                                    : 'bg-amber-50 text-amber-700'
                            }`}
                          >
                            {financialTransactionTypeLabel(
                              transaction.transactionType
                            )}
                          </span>
                        </td>

                        <td className="px-4 py-3">
                          <p className="text-xs font-medium text-foreground-800">
                            {transaction.description}
                          </p>

                          <p className="text-[10px] text-foreground-400 mt-0.5">
                            {transaction.reference
                              ? `Ref.: ${transaction.reference}`
                              : transaction.createdByName
                                ? `Por ${transaction.createdByName}`
                                : '—'}
                          </p>

                          {transaction.notes && (
                            <p className="text-[10px] text-foreground-500 mt-0.5">
                              {transaction.transactionType === 'reversal'
                                ? `Motivo: ${transaction.notes}`
                                : transaction.notes}
                            </p>
                          )}
                        </td>

                        <td className="px-4 py-3 text-xs text-foreground-600 whitespace-nowrap">
                          {getTransactionAccountsLabel(
                            transaction
                          )}
                        </td>

                        <td className="px-4 py-3">
                          <p className="text-xs text-foreground-600">
                            {transaction.categoryName ??
                              (
                                transaction.transactionType === 'transfer'
                                  ? 'Transferência interna'
                                  : '—'
                              )}
                          </p>

                          {(transaction.clientName ||
                            transaction.caseTitle) && (
                            <p className="text-[10px] text-foreground-400 mt-0.5">
                              {transaction.clientName ?? ''}
                              {transaction.clientName &&
                              transaction.caseTitle
                                ? ' · '
                                : ''}
                              {transaction.caseTitle ?? ''}
                            </p>
                          )}
                        </td>

                        <td
                          className={`px-4 py-3 text-right text-sm font-semibold whitespace-nowrap ${
                            transaction.transactionType === 'income'
                              ? 'text-emerald-600'
                              : transaction.transactionType === 'expense'
                                ? 'text-red-500'
                                : 'text-primary-700'
                          }`}
                        >
                          {transaction.transactionType === 'expense'
                            ? '− '
                            : transaction.transactionType === 'income'
                              ? '+ '
                              : ''}
                          {formatCurrencyCents(
                            transaction.amountCents
                          )}
                        </td>

                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          {transaction.transactionType === 'reversal' ? (
                            <span className="text-[10px] text-amber-600">
                              Estorno
                            </span>
                          ) : transaction.reversedById ? (
                            <span className="text-[10px] px-2 py-1 rounded-full bg-red-50 text-red-700">
                              Estornado
                            </span>
                          ) : authUser?.role !== 'secretary' &&
                            transaction.source === 'manual' ? (
                            <button
                              onClick={() =>
                                void handleReverseFinancialTransaction(
                                  transaction
                                )
                              }
                              disabled={
                                reversingFinancialTransactionId ===
                                transaction.id
                              }
                              className="text-[10px] px-2 py-1 rounded bg-red-50 text-red-700 hover:bg-red-100 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                              <i className="ri-arrow-go-back-line mr-1"></i>

                              {reversingFinancialTransactionId ===
                              transaction.id
                                ? 'Estornando...'
                                : 'Estornar'}
                            </button>
                          ) : (
                            <span className="text-[10px] text-foreground-300">
                              —
                            </span>
                          )}
                        </td>
                      </tr>
                    )
                  )
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-background-50 border border-background-200 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-background-200">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-foreground-800">
                  Conciliação Financeira
                </h3>

                <p className="text-xs text-foreground-500 mt-0.5">
                  Compare o extrato bancário com baixas, receitas, despesas e transferências registradas no sistema.
                </p>
              </div>

              <button
                onClick={openStatementEntryDrawer}
                disabled={
                  financialAccounts.length === 0
                }
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-primary-500 text-white rounded-lg text-xs font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <i className="ri-bank-line"></i>
                Adicionar extrato
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 p-4 border-b border-background-200 bg-background-100/30">
            <div>
              <p className="text-[10px] text-foreground-400 uppercase">
                Pendentes
              </p>

              <p className="text-base font-bold text-amber-600 mt-1">
                {reconciliationPendingCount}
              </p>
            </div>

            <div>
              <p className="text-[10px] text-foreground-400 uppercase">
                Parciais
              </p>

              <p className="text-base font-bold text-blue-600 mt-1">
                {reconciliationPartialCount}
              </p>
            </div>

            <div>
              <p className="text-[10px] text-foreground-400 uppercase">
                Conciliados
              </p>

              <p className="text-base font-bold text-emerald-600 mt-1">
                {reconciliationCompletedCount}
              </p>
            </div>

            <div>
              <p className="text-[10px] text-foreground-400 uppercase">
                A conciliar
              </p>

              <p className="text-base font-bold text-foreground-800 mt-1">
                {formatCurrencyCents(
                  reconciliationRemainingCents
                )}
              </p>
            </div>
          </div>

          <div className="p-4 border-b border-background-200">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
              <select
                value={reconciliationAccountFilter}
                onChange={(e) =>
                  setReconciliationAccountFilter(
                    e.target.value
                  )
                }
                className="px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-xs cursor-pointer"
              >
                <option value="">
                  Todas as contas
                </option>

                {allFinancialAccounts.map(
                  (account) => (
                    <option
                      key={account.id}
                      value={account.id}
                    >
                      {account.name}
                    </option>
                  )
                )}
              </select>

              <select
                value={reconciliationStatusFilter}
                onChange={(e) =>
                  setReconciliationStatusFilter(
                    e.target.value
                  )
                }
                className="px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-xs cursor-pointer"
              >
                <option value="">
                  Todos os status
                </option>

                <option value="pending">
                  Pendentes
                </option>

                <option value="partially_reconciled">
                  Parcialmente conciliados
                </option>

                <option value="reconciled">
                  Conciliados
                </option>

                <option value="ignored">
                  Ignorados
                </option>
              </select>

              <input
                type="date"
                value={reconciliationFromFilter}
                onChange={(e) =>
                  setReconciliationFromFilter(
                    e.target.value
                  )
                }
                className="px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-xs"
                title="Data inicial"
              />

              <input
                type="date"
                value={reconciliationToFilter}
                onChange={(e) =>
                  setReconciliationToFilter(
                    e.target.value
                  )
                }
                className="px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-xs"
                title="Data final"
              />
            </div>
          </div>

          {reconciliationError && (
            <div className="m-4 p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
              {reconciliationError}
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-background-100/50 border-b border-background-200">
                  <th className="text-left px-4 py-3 text-[10px] uppercase text-foreground-500">
                    Data
                  </th>

                  <th className="text-left px-4 py-3 text-[10px] uppercase text-foreground-500">
                    Conta
                  </th>

                  <th className="text-left px-4 py-3 text-[10px] uppercase text-foreground-500">
                    Descrição
                  </th>

                  <th className="text-right px-4 py-3 text-[10px] uppercase text-foreground-500">
                    Valor
                  </th>

                  <th className="text-right px-4 py-3 text-[10px] uppercase text-foreground-500">
                    Conciliado
                  </th>

                  <th className="text-left px-4 py-3 text-[10px] uppercase text-foreground-500">
                    Status
                  </th>

                  <th className="text-right px-4 py-3 text-[10px] uppercase text-foreground-500">
                    Ação
                  </th>
                </tr>
              </thead>

              <tbody>
                {isLoadingStatementEntries ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-4 py-8 text-center text-xs text-foreground-500"
                    >
                      Carregando extrato...
                    </td>
                  </tr>
                ) : statementEntries.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-4 py-8 text-center text-xs text-foreground-500"
                    >
                      Nenhum lançamento bancário encontrado.
                    </td>
                  </tr>
                ) : (
                  statementEntries.map(
                    (entry) => (
                      <tr
                        key={entry.id}
                        className="border-b border-background-100 hover:bg-background-50"
                      >
                        <td className="px-4 py-3 text-xs text-foreground-600 whitespace-nowrap">
                          {formatDate(
                            entry.postedAt
                          )}
                        </td>

                        <td className="px-4 py-3 text-xs text-foreground-600 whitespace-nowrap">
                          {entry.accountName}
                        </td>

                        <td className="px-4 py-3">
                          <p className="text-xs font-medium text-foreground-800">
                            {entry.description}
                          </p>

                          {entry.reference && (
                            <p className="text-[10px] text-foreground-400 mt-0.5">
                              Ref.:{' '}
                              {entry.reference}
                            </p>
                          )}
                        </td>

                        <td
                          className={`px-4 py-3 text-right text-sm font-semibold whitespace-nowrap ${
                            entry.amountCents >= 0
                              ? 'text-emerald-600'
                              : 'text-red-500'
                          }`}
                        >
                          {entry.amountCents >= 0
                            ? '+ '
                            : '− '}

                          {formatCurrencyCents(
                            Math.abs(
                              entry.amountCents
                            )
                          )}
                        </td>

                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <p className="text-xs font-semibold text-foreground-700">
                            {formatCurrencyCents(
                              entry.matchedCents
                            )}
                          </p>

                          {entry.remainingCents > 0 && (
                            <p className="text-[10px] text-foreground-400 mt-0.5">
                              Falta{' '}
                              {formatCurrencyCents(
                                entry.remainingCents
                              )}
                            </p>
                          )}
                        </td>

                        <td className="px-4 py-3">
                          <span
                            className={`text-[10px] px-2 py-1 rounded-full ${
                              entry.status === 'reconciled'
                                ? 'bg-emerald-50 text-emerald-700'
                                : entry.status === 'partially_reconciled'
                                  ? 'bg-blue-50 text-blue-700'
                                  : entry.status === 'ignored'
                                    ? 'bg-background-200 text-foreground-500'
                                    : 'bg-amber-50 text-amber-700'
                            }`}
                          >
                            {reconciliationStatusLabel(
                              entry.status
                            )}
                          </span>
                        </td>

                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          {entry.status === 'reconciled' ? (
                            <span className="text-[10px] text-emerald-600">
                              <i className="ri-check-line mr-1"></i>
                              OK
                            </span>
                          ) : entry.status === 'ignored' ? (
                            <span className="text-[10px] text-foreground-400">
                              —
                            </span>
                          ) : (
                            <button
                              onClick={() =>
                                void openReconciliationDrawer(
                                  entry
                                )
                              }
                              className="text-[10px] px-2 py-1 rounded bg-primary-50 text-primary-700 hover:bg-primary-100 transition-colors cursor-pointer"
                            >
                              <i className="ri-links-line mr-1"></i>
                              Conciliar
                            </button>
                          )}
                        </td>
                      </tr>
                    )
                  )
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-background-50 border border-background-200 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-background-200">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-foreground-800">
                  Fechamento Mensal
                </h3>

                <p className="text-xs text-foreground-500 mt-0.5">
                  Consolidação financeira por competência, com fotografia dos saldos e histórico auditável.
                </p>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <input
                  type="month"
                  value={financialClosingPeriod}
                  onChange={(e) =>
                    setFinancialClosingPeriod(
                      e.target.value
                    )
                  }
                  className="px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-xs"
                />

                {selectedFinancialMonth?.status === 'closed' ? (
                  authUser?.role === 'admin' && (
                    <button
                      onClick={() =>
                        void handleReopenFinancialMonth()
                      }
                      disabled={isProcessingFinancialClosing}
                      className="inline-flex items-center gap-1.5 px-3 py-2 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg text-xs font-semibold hover:bg-amber-100 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      <i className="ri-lock-unlock-line"></i>

                      {isProcessingFinancialClosing
                        ? 'Processando...'
                        : 'Reabrir competência'}
                    </button>
                  )
                ) : authUser?.role !== 'secretary' ? (
                  <button
                    onClick={() =>
                      void handleCloseFinancialMonth()
                    }
                    disabled={isProcessingFinancialClosing}
                    className="inline-flex items-center gap-1.5 px-3 py-2 bg-primary-500 text-white rounded-lg text-xs font-semibold hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <i className="ri-lock-line"></i>

                    {isProcessingFinancialClosing
                      ? 'Processando...'
                      : 'Fechar competência'}
                  </button>
                ) : null}
              </div>
            </div>
          </div>

          {financialMonthError && (
            <div className="m-4 p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
              {financialMonthError}
            </div>
          )}

          {isLoadingFinancialMonth ? (
            <div className="p-8 text-center text-xs text-foreground-500">
              Carregando competência...
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 p-4 border-b border-background-200 bg-background-100/30">
                <div>
                  <p className="text-[10px] uppercase text-foreground-400">
                    Competência
                  </p>

                  <p className="text-sm font-bold text-foreground-800 mt-1">
                    {financialClosingPeriod || '—'}
                  </p>
                </div>

                <div>
                  <p className="text-[10px] uppercase text-foreground-400">
                    Situação
                  </p>

                  <div className="mt-1">
                    <span
                      className={`text-[10px] px-2 py-1 rounded-full ${
                        selectedFinancialMonth?.status === 'closed'
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'bg-blue-50 text-blue-700'
                      }`}
                    >
                      {selectedFinancialMonth?.status === 'closed'
                        ? 'Fechada'
                        : 'Aberta'}
                    </span>
                  </div>
                </div>

                <div>
                  <p className="text-[10px] uppercase text-foreground-400">
                    Saldo fotografado
                  </p>

                  <p className="text-sm font-bold text-foreground-800 mt-1">
                    {financialMonthSnapshots.length > 0
                      ? formatCurrencyCents(
                          financialClosingTotalCents
                        )
                      : '—'}
                  </p>
                </div>

                <div>
                  <p className="text-[10px] uppercase text-foreground-400">
                    Pendências de conciliação
                  </p>

                  <p
                    className={`text-sm font-bold mt-1 ${
                      (
                        selectedFinancialMonth
                          ?.pendingReconciliationCount ??
                        0
                      ) > 0
                        ? 'text-amber-600'
                        : 'text-emerald-600'
                    }`}
                  >
                    {selectedFinancialMonth
                      ?.pendingReconciliationCount ??
                      0}

                    {(selectedFinancialMonth
                      ?.pendingReconciliationAmountCents ??
                      0) > 0 && (
                      <span className="text-[10px] font-normal ml-1">
                        ·{' '}
                        {formatCurrencyCents(
                          selectedFinancialMonth
                            ?.pendingReconciliationAmountCents ??
                            0
                        )}
                      </span>
                    )}
                  </p>
                </div>
              </div>

              {selectedFinancialMonth?.status !== 'closed' &&
                (
                  selectedFinancialMonth
                    ?.pendingReconciliationCount ??
                  0
                ) > 0 && (
                  <div className="m-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
                    O fechamento está bloqueado enquanto existirem lançamentos bancários pendentes ou parcialmente conciliados nesta competência.
                  </div>
                )}

              <div className="p-4 border-b border-background-200">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="text-xs font-semibold text-foreground-700">
                      Fotografia das contas
                    </h4>

                    <p className="text-[10px] text-foreground-400 mt-0.5">
                      Valores preservados no momento do último fechamento da competência.
                    </p>
                  </div>
                </div>

                {financialMonthSnapshots.length === 0 ? (
                  <div className="py-6 text-center text-xs text-foreground-500 border border-dashed border-background-200 rounded-lg">
                    A competência ainda não possui fotografia de fechamento.
                  </div>
                ) : (
                  <div className="overflow-x-auto border border-background-200 rounded-lg">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-background-100/50 border-b border-background-200">
                          <th className="text-left px-3 py-2 text-[10px] uppercase text-foreground-500">
                            Conta
                          </th>

                          <th className="text-right px-3 py-2 text-[10px] uppercase text-foreground-500">
                            Saldo inicial
                          </th>

                          <th className="text-right px-3 py-2 text-[10px] uppercase text-foreground-500">
                            Recebimentos
                          </th>

                          <th className="text-right px-3 py-2 text-[10px] uppercase text-foreground-500">
                            Estornos
                          </th>

                          <th className="text-right px-3 py-2 text-[10px] uppercase text-foreground-500">
                            Livro Caixa
                          </th>

                          <th className="text-right px-3 py-2 text-[10px] uppercase text-foreground-500">
                            Saldo final
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {financialMonthSnapshots.map(
                          (snapshot) => (
                            <tr
                              key={snapshot.id}
                              className="border-b border-background-100 last:border-b-0"
                            >
                              <td className="px-3 py-2 text-xs font-medium text-foreground-700">
                                {snapshot.accountName}
                              </td>

                              <td className="px-3 py-2 text-xs text-right text-foreground-600">
                                {formatCurrencyCents(
                                  snapshot.balanceBeforeCents
                                )}
                              </td>

                              <td className="px-3 py-2 text-xs text-right text-emerald-600">
                                {formatCurrencyCents(
                                  snapshot.settlementReceiptsCents
                                )}
                              </td>

                              <td className="px-3 py-2 text-xs text-right text-red-500">
                                {formatCurrencyCents(
                                  snapshot.settlementReversalsCents
                                )}
                              </td>

                              <td
                                className={`px-3 py-2 text-xs text-right ${
                                  snapshot.ledgerNetCents >= 0
                                    ? 'text-emerald-600'
                                    : 'text-red-500'
                                }`}
                              >
                                {formatCurrencyCents(
                                  snapshot.ledgerNetCents
                                )}
                              </td>

                              <td className="px-3 py-2 text-xs text-right font-bold text-foreground-800">
                                {formatCurrencyCents(
                                  snapshot.closingBalanceCents
                                )}
                              </td>
                            </tr>
                          )
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="p-4">
                <h4 className="text-xs font-semibold text-foreground-700 mb-3">
                  Histórico da competência
                </h4>

                {financialMonthEvents.length === 0 ? (
                  <p className="text-xs text-foreground-500">
                    Nenhum fechamento ou reabertura registrado.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {financialMonthEvents.map(
                      (event) => (
                        <div
                          key={event.id}
                          className="flex items-start justify-between gap-3 p-3 border border-background-200 rounded-lg"
                        >
                          <div>
                            <div className="flex items-center gap-2">
                              <span
                                className={`text-[10px] px-2 py-1 rounded-full ${
                                  event.action === 'close'
                                    ? 'bg-emerald-50 text-emerald-700'
                                    : 'bg-amber-50 text-amber-700'
                                }`}
                              >
                                {event.action === 'close'
                                  ? 'Fechamento'
                                  : 'Reabertura'}
                              </span>

                              <span className="text-[10px] text-foreground-400">
                                {formatDate(
                                  event.createdAt
                                )}
                              </span>
                            </div>

                            {event.reason && (
                              <p className="text-xs text-foreground-600 mt-2">
                                {event.reason}
                              </p>
                            )}
                          </div>

                          <p className="text-[10px] text-foreground-400 text-right">
                            {event.createdByName
                              ? `Por ${event.createdByName}`
                              : 'Usuário não identificado'}
                          </p>
                        </div>
                      )
                    )}
                  </div>
                )}
              </div>

              {financialMonthPeriods.length > 0 && (
                <div className="px-4 pb-4">
                  <p className="text-[10px] uppercase text-foreground-400 mb-2">
                    Competências já registradas
                  </p>

                  <div className="flex gap-1.5 flex-wrap">
                    {financialMonthPeriods
                      .slice(0, 12)
                      .map(
                        (period) => (
                          <button
                            key={period.periodKey}
                            onClick={() =>
                              setFinancialClosingPeriod(
                                period.periodKey
                              )
                            }
                            className={`text-[10px] px-2.5 py-1.5 rounded-lg border cursor-pointer transition-colors ${
                              financialClosingPeriod === period.periodKey
                                ? 'bg-primary-50 text-primary-700 border-primary-200'
                                : 'bg-background-50 text-foreground-600 border-background-200 hover:bg-background-100'
                            }`}
                          >
                            {period.periodKey}

                            {' · '}

                            {period.status === 'closed'
                              ? 'Fechada'
                              : 'Aberta'}
                          </button>
                        )
                      )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="bg-background-50 border border-background-200 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-background-200">
            <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-foreground-800">
                  Relatórios Financeiros
                </h3>

                <p className="text-xs text-foreground-500 mt-0.5">
                  Visão consolidada de recebimentos, resultado, inadimplência, contas, categorias e evolução financeira.
                </p>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <input
                  type="date"
                  value={financialReportFrom}
                  onChange={(e) =>
                    setFinancialReportFrom(
                      e.target.value
                    )
                  }
                  className="px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-xs"
                  title="Data inicial"
                />

                <input
                  type="date"
                  value={financialReportTo}
                  onChange={(e) =>
                    setFinancialReportTo(
                      e.target.value
                    )
                  }
                  className="px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-xs"
                  title="Data final"
                />

                <select
                  value={financialReportAccountId}
                  onChange={(e) =>
                    setFinancialReportAccountId(
                      e.target.value
                    )
                  }
                  className="px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-xs cursor-pointer"
                >
                  <option value="">
                    Todas as contas
                  </option>

                  {allFinancialAccounts.map(
                    (account) => (
                      <option
                        key={account.id}
                        value={account.id}
                      >
                        {account.name}
                      </option>
                    )
                  )}
                </select>

                {financialReport && (
                  <button
                    onClick={() =>
                      downloadJsonFile(
                        `relatorio-financeiro-${financialReport.from}-${financialReport.to}.json`,
                        financialReport
                      )
                    }
                    className="inline-flex items-center gap-1.5 px-3 py-2 border border-background-200 rounded-lg text-xs font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <i className="ri-download-line"></i>
                    Exportar
                  </button>
                )}
              </div>
            </div>
          </div>

          {financialReportError && (
            <div className="m-4 p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
              {financialReportError}
            </div>
          )}

          {isLoadingFinancialReport ? (
            <div className="p-8 text-center text-xs text-foreground-500">
              Gerando relatório financeiro...
            </div>
          ) : !financialReport ? (
            <div className="p-8 text-center text-xs text-foreground-500">
              Nenhum relatório disponível.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 p-4 border-b border-background-200 bg-background-100/30">
                <div>
                  <p className="text-[10px] uppercase text-foreground-400">
                    Recebimentos
                  </p>

                  <p className="text-sm font-bold text-emerald-600 mt-1">
                    {formatCurrencyCents(
                      financialReport.summary.netSettlementsCents
                    )}
                  </p>

                  {financialReport.summary.settlementReversalsCents > 0 && (
                    <p className="text-[9px] text-foreground-400 mt-0.5">
                      Estornos:{' '}
                      {formatCurrencyCents(
                        financialReport.summary.settlementReversalsCents
                      )}
                    </p>
                  )}
                </div>

                <div>
                  <p className="text-[10px] uppercase text-foreground-400">
                    Livro Caixa
                  </p>

                  <p
                    className={`text-sm font-bold mt-1 ${
                      financialReport.summary.operationalLedgerCents >= 0
                        ? 'text-emerald-600'
                        : 'text-red-500'
                    }`}
                  >
                    {formatCurrencyCents(
                      financialReport.summary.operationalLedgerCents
                    )}
                  </p>
                </div>

                <div>
                  <p className="text-[10px] uppercase text-foreground-400">
                    Resultado operacional
                  </p>

                  <p
                    className={`text-sm font-bold mt-1 ${
                      financialReport.summary.operationalResultCents >= 0
                        ? 'text-emerald-700'
                        : 'text-red-600'
                    }`}
                  >
                    {formatCurrencyCents(
                      financialReport.summary.operationalResultCents
                    )}
                  </p>
                </div>

                <div>
                  <p className="text-[10px] uppercase text-foreground-400">
                    Movimento líquido
                  </p>

                  <p
                    className={`text-sm font-bold mt-1 ${
                      financialReport.summary.cashNetCents >= 0
                        ? 'text-foreground-800'
                        : 'text-red-600'
                    }`}
                  >
                    {formatCurrencyCents(
                      financialReport.summary.cashNetCents
                    )}
                  </p>
                </div>

                <div>
                  <p className="text-[10px] uppercase text-foreground-400">
                    A receber
                  </p>

                  <p className="text-sm font-bold text-primary-700 mt-1">
                    {formatCurrencyCents(
                      financialReport.receivables.outstandingCents
                    )}
                  </p>
                </div>

                <div>
                  <p className="text-[10px] uppercase text-foreground-400">
                    Inadimplência
                  </p>

                  <p
                    className={`text-sm font-bold mt-1 ${
                      financialReport.receivables.overdueCents > 0
                        ? 'text-red-600'
                        : 'text-emerald-600'
                    }`}
                  >
                    {formatCurrencyCents(
                      financialReport.receivables.overdueCents
                    )}
                  </p>

                  <p className="text-[9px] text-foreground-400 mt-0.5">
                    {financialReport.receivables.overdueCount}{' '}
                    parcela(s) vencida(s)
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 p-4 border-b border-background-200">
                <div>
                  <div className="mb-3">
                    <h4 className="text-xs font-semibold text-foreground-700">
                      Desempenho por conta
                    </h4>

                    <p className="text-[10px] text-foreground-400 mt-0.5">
                      Entradas, saídas e movimentação líquida no período.
                    </p>
                  </div>

                  {financialReport.byAccount.length === 0 ? (
                    <p className="text-xs text-foreground-500">
                      Nenhuma movimentação por conta.
                    </p>
                  ) : (
                    <div className="overflow-x-auto border border-background-200 rounded-lg">
                      <table className="w-full">
                        <thead>
                          <tr className="bg-background-100/50 border-b border-background-200">
                            <th className="text-left px-3 py-2 text-[10px] uppercase text-foreground-500">
                              Conta
                            </th>

                            <th className="text-right px-3 py-2 text-[10px] uppercase text-foreground-500">
                              Recebimentos
                            </th>

                            <th className="text-right px-3 py-2 text-[10px] uppercase text-foreground-500">
                              Entradas
                            </th>

                            <th className="text-right px-3 py-2 text-[10px] uppercase text-foreground-500">
                              Saídas
                            </th>

                            <th className="text-right px-3 py-2 text-[10px] uppercase text-foreground-500">
                              Líquido
                            </th>
                          </tr>
                        </thead>

                        <tbody>
                          {financialReport.byAccount.map(
                            (account) => (
                              <tr
                                key={account.id}
                                className="border-b border-background-100 last:border-b-0"
                              >
                                <td className="px-3 py-2 text-xs font-medium text-foreground-700">
                                  {account.name}
                                </td>

                                <td className="px-3 py-2 text-xs text-right text-emerald-600">
                                  {formatCurrencyCents(
                                    account.settlementReceiptsCents -
                                    account.settlementReversalsCents
                                  )}
                                </td>

                                <td className="px-3 py-2 text-xs text-right text-emerald-600">
                                  {formatCurrencyCents(
                                    account.ledgerInCents
                                  )}
                                </td>

                                <td className="px-3 py-2 text-xs text-right text-red-500">
                                  {formatCurrencyCents(
                                    account.ledgerOutCents
                                  )}
                                </td>

                                <td
                                  className={`px-3 py-2 text-xs text-right font-semibold ${
                                    account.netCents >= 0
                                      ? 'text-foreground-800'
                                      : 'text-red-600'
                                  }`}
                                >
                                  {formatCurrencyCents(
                                    account.netCents
                                  )}
                                </td>
                              </tr>
                            )
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                <div>
                  <div className="mb-3">
                    <h4 className="text-xs font-semibold text-foreground-700">
                      Resultado por categoria
                    </h4>

                    <p className="text-[10px] text-foreground-400 mt-0.5">
                      Receitas e despesas classificadas no Livro Caixa.
                    </p>
                  </div>

                  {financialReport.byCategory.length === 0 ? (
                    <p className="text-xs text-foreground-500">
                      Nenhuma categoria movimentada.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {financialReport.byCategory.map(
                        (category) => (
                          <div
                            key={category.id}
                            className="flex items-center justify-between gap-3 p-3 border border-background-200 rounded-lg"
                          >
                            <div>
                              <p className="text-xs font-medium text-foreground-700">
                                {category.name}
                              </p>

                              <p className="text-[10px] text-foreground-400 mt-0.5">
                                {category.transactionCount}{' '}
                                movimentação(ões)
                              </p>
                            </div>

                            <p
                              className={`text-sm font-bold ${
                                category.netCents >= 0
                                  ? 'text-emerald-600'
                                  : 'text-red-500'
                              }`}
                            >
                              {formatCurrencyCents(
                                category.netCents
                              )}
                            </p>
                          </div>
                        )
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 p-4 border-b border-background-200">
                <div>
                  <div className="mb-3">
                    <h4 className="text-xs font-semibold text-foreground-700">
                      Recebimentos por tipo
                    </h4>

                    <p className="text-[10px] text-foreground-400 mt-0.5">
                      Honorários contratuais, êxito, sucumbência, reembolsos e outros.
                    </p>
                  </div>

                  {financialReport.byChargeType.length === 0 ? (
                    <p className="text-xs text-foreground-500">
                      Nenhum recebimento no período.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {financialReport.byChargeType.map(
                        (item) => (
                          <div
                            key={item.chargeType}
                            className="p-3 border border-background-200 rounded-lg"
                          >
                            <div className="flex items-center justify-between gap-3">
                              <p className="text-xs font-medium text-foreground-700">
                                {financialChargeTypeLabel(
                                  item.chargeType
                                )}
                              </p>

                              <p className="text-sm font-bold text-emerald-600">
                                {formatCurrencyCents(
                                  item.netReceivedCents
                                )}
                              </p>
                            </div>

                            {item.reversalCents > 0 && (
                              <p className="text-[10px] text-foreground-400 mt-1">
                                Recebido:{' '}
                                {formatCurrencyCents(
                                  item.receivedCents
                                )}
                                {' · '}
                                Estornado:{' '}
                                {formatCurrencyCents(
                                  item.reversalCents
                                )}
                              </p>
                            )}
                          </div>
                        )
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <div className="mb-3">
                    <h4 className="text-xs font-semibold text-foreground-700">
                      Evolução mensal
                    </h4>

                    <p className="text-[10px] text-foreground-400 mt-0.5">
                      Resultado operacional e movimentação líquida por competência.
                    </p>
                  </div>

                  {financialReport.monthly.length === 0 ? (
                    <p className="text-xs text-foreground-500">
                      Nenhum mês com movimentação no período.
                    </p>
                  ) : (
                    <div className="overflow-x-auto border border-background-200 rounded-lg">
                      <table className="w-full">
                        <thead>
                          <tr className="bg-background-100/50 border-b border-background-200">
                            <th className="text-left px-3 py-2 text-[10px] uppercase text-foreground-500">
                              Mês
                            </th>

                            <th className="text-right px-3 py-2 text-[10px] uppercase text-foreground-500">
                              Recebimentos
                            </th>

                            <th className="text-right px-3 py-2 text-[10px] uppercase text-foreground-500">
                              Livro Caixa
                            </th>

                            <th className="text-right px-3 py-2 text-[10px] uppercase text-foreground-500">
                              Resultado
                            </th>

                            <th className="text-right px-3 py-2 text-[10px] uppercase text-foreground-500">
                              Líquido
                            </th>
                          </tr>
                        </thead>

                        <tbody>
                          {financialReport.monthly.map(
                            (month) => (
                              <tr
                                key={month.monthKey}
                                className="border-b border-background-100 last:border-b-0"
                              >
                                <td className="px-3 py-2 text-xs font-medium text-foreground-700">
                                  {month.monthKey}
                                </td>

                                <td className="px-3 py-2 text-xs text-right text-emerald-600">
                                  {formatCurrencyCents(
                                    month.settlementNetCents
                                  )}
                                </td>

                                <td
                                  className={`px-3 py-2 text-xs text-right ${
                                    month.operationalLedgerCents >= 0
                                      ? 'text-emerald-600'
                                      : 'text-red-500'
                                  }`}
                                >
                                  {formatCurrencyCents(
                                    month.operationalLedgerCents
                                  )}
                                </td>

                                <td
                                  className={`px-3 py-2 text-xs text-right font-semibold ${
                                    month.operationalResultCents >= 0
                                      ? 'text-foreground-800'
                                      : 'text-red-600'
                                  }`}
                                >
                                  {formatCurrencyCents(
                                    month.operationalResultCents
                                  )}
                                </td>

                                <td
                                  className={`px-3 py-2 text-xs text-right font-semibold ${
                                    month.cashNetCents >= 0
                                      ? 'text-foreground-800'
                                      : 'text-red-600'
                                  }`}
                                >
                                  {formatCurrencyCents(
                                    month.cashNetCents
                                  )}
                                </td>
                              </tr>
                            )
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>

              <div className="px-4 py-3 bg-background-100/30 text-[10px] text-foreground-400 flex items-center justify-between gap-3 flex-wrap">
                <span>
                  Período: {formatDate(financialReport.from)} até{' '}
                  {formatDate(financialReport.to)}
                </span>

                <span>
                  Gerado em {formatDate(financialReport.generatedAt)}
                </span>
              </div>
            </>
          )}
        </div>

        <div className="flex items-center gap-1.5 flex-wrap">
          {views.map((v) => (
            <button
              key={v.key}
              onClick={() => setViewMode(v.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                viewMode === v.key ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              {v.label}
            </button>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
          <SearchInput value={search} onChange={setSearch} placeholder="Buscar por cliente, caso ou regra..." className="sm:w-72" />
          <FilterBar
            filters={[
              {
                key: 'status',
                label: 'Status',
                options: paymentStatusFilterOptions,
                value: statusFilter,
                onChange: setStatusFilter,
              },

              {
                key: 'chargeType',
                label: 'Natureza',
                options:
                  paymentChargeTypeFilterOptions,
                value:
                  chargeTypeFilter,
                onChange:
                  setChargeTypeFilter,
              },
            ]}
          />
        </div>

        {paymentsError && (
          <div className="flex items-center justify-between gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-center gap-2 text-sm text-amber-700">
              <i className="ri-alert-line"></i>
              <span>{paymentsError}</span>
            </div>
            <button
              onClick={handleRetryPayments}
              className="text-xs font-medium text-amber-700 hover:text-amber-800 cursor-pointer whitespace-nowrap"
            >
              Tentar novamente
            </button>
          </div>
        )}

        <div className="bg-background-50 border border-background-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-background-200 bg-background-100/50">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase whitespace-nowrap">Cliente</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase whitespace-nowrap hidden md:table-cell">
                    Caso / Regra
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase whitespace-nowrap">Total</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase whitespace-nowrap hidden lg:table-cell">
                    Pago
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase whitespace-nowrap hidden lg:table-cell">
                    Saldo
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase whitespace-nowrap hidden xl:table-cell">
                    Primeira parcela
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase whitespace-nowrap">Status</th>
                  <th className="w-10 px-2 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {isLoadingPayments && displayPayments.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-sm text-foreground-500">
                      Carregando pagamentos...
                    </td>
                  </tr>
                ) : displayPayments.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-sm text-foreground-500">
                      Nenhum lançamento encontrado.
                    </td>
                  </tr>
                ) : (
                  displayPayments.map((payment) => (
                    <Fragment key={payment.id}>
                      <tr
                        className="border-b border-background-100 hover:bg-background-50 transition-colors cursor-pointer"
                        onClick={() => handleTogglePayment(payment)}
                      >
                        <td className="px-4 py-3">
                          <p className="text-sm font-medium text-foreground-800 whitespace-nowrap">{payment.clientName ?? '—'}</p>
                          <p className="text-[11px] text-foreground-400 mt-0.5 whitespace-nowrap">{payment.title}</p>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <p className="text-sm text-foreground-700 whitespace-nowrap">{payment.caseTitle ?? '—'}</p>
                          <p className="text-[11px] text-foreground-400 mt-0.5 whitespace-nowrap">
                            {payment.feeRuleName ?? 'Sem regra vinculada'}
                          </p>

                          <span className="inline-flex mt-1 px-2 py-0.5 rounded-full bg-primary-50 text-primary-700 text-[9px] font-medium whitespace-nowrap">
                            {financialChargeTypeLabel(
                              payment.chargeType
                            )}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-sm font-semibold text-foreground-800 whitespace-nowrap">{formatCurrencyCents(payment.totalAmountCents)}</span>
                        </td>
                        <td className="px-4 py-3 text-right hidden lg:table-cell">
                          <span className="text-sm text-emerald-600 font-medium whitespace-nowrap">
                            {formatCurrencyCents(payment.paidAmountCents ?? 0)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right hidden lg:table-cell">
                          <span className={`text-sm font-medium whitespace-nowrap ${(payment.balanceAmountCents ?? 0) > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                            {formatCurrencyCents(payment.balanceAmountCents ?? 0)}
                          </span>
                        </td>
                        <td className="px-4 py-3 hidden xl:table-cell">
                          <span className="text-sm text-foreground-600 whitespace-nowrap">{formatDate(payment.firstDueDate)}</span>
                        </td>
                        <td className="px-4 py-3">
                          <PaymentStatusBadge status={payment.status} />
                        </td>
                        <td className="px-2 py-3">
                          <i className={`${expandedId === payment.id ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-foreground-400`}></i>
                        </td>
                      </tr>
                      {expandedId === payment.id && (
                        <tr key={`${payment.id}-inst`}>
                          <td colSpan={8} className="px-4 py-2 bg-background-50">
                            <div className="space-y-1.5">
                              <p className="text-[10px] text-foreground-400 uppercase font-semibold">Parcelas</p>
                              <div className="flex gap-2 flex-wrap">
                                {payment.installments.map((installment) => (
                                  <div
                                    key={installment.id}
                                    className={`px-3 py-2 rounded-lg text-xs border ${
                                      installment.status === 'overdue'
                                        ? 'bg-red-50 border-red-200'
                                        : installment.status === 'paid'
                                          ? 'bg-emerald-50 border-emerald-200'
                                          : 'bg-background-100 border-background-200'
                                    }`}
                                  >
                                    <p className="font-semibold text-foreground-800">
                                      {installment.installmentNumber}ª — {formatCurrencyCents(installment.amountCents)}
                                    </p>
                                    <p
                                      className={`${
                                        installment.status === 'overdue'
                                          ? 'text-red-600'
                                          : installment.status === 'paid'
                                            ? 'text-emerald-600'
                                            : 'text-amber-600'
                                      }`}
                                    >
                                      {formatDate(installment.dueDate)} - {getInstallmentStatusLabel(installment.status)}
                                    </p>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openSettlementDrawer(payment, installment);
                                      }}
                                      disabled={installment.status === 'paid' || installmentActionId === installment.id || isSavingPayment}
                                      className="mt-2 text-[10px] px-2 py-1 rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                                    >
                                      <i className="ri-check-line mr-0.5"></i>
                                      {installmentActionId === installment.id
                                        ? 'Salvando...'
                                        : installment.status === 'paid'
                                          ? 'Pago'
                                          : 'Registrar baixa'}
                                    </button>
                                  </div>
                                ))}
                              </div>
                              <div className="flex flex-wrap items-center gap-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openEditChargeDrawer(payment);
                                  }}
                                  disabled={payment.status === 'archived' || payment.status === 'cancelled' || isPaymentsActionBusy}
                                  className="text-[10px] px-2 py-1 rounded bg-background-100 text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                  <i className="ri-edit-line mr-0.5"></i>Editar
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    void handleArchivePayment(payment);
                                  }}
                                  disabled={isPaymentsActionBusy || payment.status === 'archived'}
                                  className="text-[10px] px-2 py-1 rounded bg-red-50 text-red-700 hover:bg-red-100 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                  <i className="ri-archive-line mr-0.5"></i>
                                  {paymentActionId === payment.id
                                    ? 'Processando...'
                                    : payment.status === 'archived'
                                      ? 'Arquivado'
                                      : 'Arquivar'}
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    void handleCancelPayment(payment);
                                  }}
                                  disabled={isPaymentsActionBusy || payment.status === 'cancelled'}
                                  className="text-[10px] px-2 py-1 rounded bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                  <i className="ri-close-circle-line mr-0.5"></i>
                                  {paymentActionId === payment.id
                                    ? 'Processando...'
                                    : payment.status === 'cancelled'
                                      ? 'Cancelado'
                                      : 'Cancelar'}
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    void handleRegisterPaymentShortcut(payment);
                                  }}
                                  disabled={
                                    isPaymentsActionBusy ||
                                    payment.status === 'archived' ||
                                    payment.status === 'cancelled' ||
                                    (payment.balanceAmountCents ?? 0) <= 0
                                  }
                                  className="text-[10px] px-2 py-1 rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                  <i className="ri-check-line mr-0.5"></i>Registrar Pagamento
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    addToast("Link de cobrança em breve. Esta ação ainda não está disponível.", "info");
                                  }}
                                  className="text-[10px] px-2 py-1 rounded bg-primary-50 text-primary-700 hover:bg-primary-100 transition-colors cursor-pointer whitespace-nowrap"
                                >
                                  <i className="ri-link mr-0.5"></i>Link de Cobrança <span className="ml-0.5 rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">Futuro</span>
                                </button>
                              </div>

                              <div className="mt-4 pt-3 border-t border-background-200">
                                <div className="flex items-center justify-between gap-2 mb-2">
                                  <p className="text-[10px] text-foreground-400 uppercase font-semibold">
                                    Histórico de Baixas
                                  </p>

                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      void loadPaymentSettlementHistory(payment.id);
                                    }}
                                    disabled={settlementHistoryLoadingId === payment.id}
                                    className="text-[10px] text-primary-600 hover:text-primary-700 cursor-pointer disabled:opacity-60"
                                  >
                                    <i className="ri-refresh-line mr-1"></i>
                                    Atualizar
                                  </button>
                                </div>

                                {settlementHistoryLoadingId === payment.id ? (
                                  <div className="py-3 text-xs text-foreground-500">
                                    Carregando histórico financeiro...
                                  </div>
                                ) : (settlementHistoryByPaymentId[payment.id] ?? []).length === 0 ? (
                                  <div className="py-3 text-xs text-foreground-500">
                                    Nenhuma baixa registrada nesta cobrança.
                                  </div>
                                ) : (
                                  <div className="space-y-2">
                                    {(settlementHistoryByPaymentId[payment.id] ?? []).map((settlement) => {
                                      const history =
                                        settlementHistoryByPaymentId[payment.id] ?? [];

                                      const alreadyReversed =
                                        history.some(
                                          (item) =>
                                            item.kind === 'reversal' &&
                                            item.reversalOfId === settlement.id
                                        );

                                      const canReverse =
                                        settlement.kind === 'receipt' &&
                                        settlement.source !== 'legacy_backfill' &&
                                        settlement.source !== 'asaas' &&
                                        !alreadyReversed;

                                      const paymentMethodLabel =
                                        paymentMethodOptions.find(
                                          (option) =>
                                            option.value === settlement.paymentMethod
                                        )?.label ??
                                        settlement.paymentMethod ??
                                        '—';

                                      return (
                                        <div
                                          key={settlement.id}
                                          className={`p-3 rounded-lg border ${
                                            settlement.kind === 'reversal'
                                              ? 'bg-red-50 border-red-200'
                                              : alreadyReversed
                                                ? 'bg-background-100 border-background-200 opacity-75'
                                                : 'bg-emerald-50 border-emerald-200'
                                          }`}
                                        >
                                          <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0">
                                              <div className="flex items-center gap-2 flex-wrap">
                                                <span
                                                  className={`text-xs font-semibold ${
                                                    settlement.kind === 'reversal'
                                                      ? 'text-red-700'
                                                      : 'text-emerald-700'
                                                  }`}
                                                >
                                                  {settlement.kind === 'reversal'
                                                    ? 'Estorno'
                                                    : 'Recebimento'}
                                                </span>

                                                {alreadyReversed && settlement.kind === 'receipt' && (
                                                  <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700">
                                                    Estornado
                                                  </span>
                                                )}

                                                {settlement.source === 'legacy_backfill' && (
                                                  <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-background-200 text-foreground-600">
                                                    Histórico importado
                                                  </span>
                                                )}
                                              </div>

                                              <p
                                                className={`text-sm font-bold mt-1 ${
                                                  settlement.kind === 'reversal'
                                                    ? 'text-red-700'
                                                    : 'text-emerald-700'
                                                }`}
                                              >
                                                {settlement.kind === 'reversal' ? '− ' : '+ '}
                                                {formatCurrencyCents(settlement.amountCents)}
                                              </p>
                                            </div>

                                            {canReverse && (
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  void handleReverseSettlement(
                                                    payment,
                                                    settlement
                                                  );
                                                }}
                                                disabled={reversingSettlementId === settlement.id}
                                                className="text-[10px] px-2 py-1 rounded bg-red-100 text-red-700 hover:bg-red-200 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                                              >
                                                <i className="ri-arrow-go-back-line mr-1"></i>
                                                {reversingSettlementId === settlement.id
                                                  ? 'Estornando...'
                                                  : 'Estornar'}
                                              </button>
                                            )}
                                          </div>

                                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-1 mt-2 text-[10px] text-foreground-600">
                                            <span>
                                              <strong>Data:</strong>{' '}
                                              {formatDate(settlement.occurredAt)}
                                            </span>

                                            <span>
                                              <strong>Forma:</strong>{' '}
                                              {paymentMethodLabel}
                                            </span>

                                            <span>
                                              <strong>Conta:</strong>{' '}
                                              {settlement.financialAccountName ?? '—'}
                                            </span>

                                            <span>
                                              <strong>Responsável:</strong>{' '}
                                              {settlement.createdByName ?? '—'}
                                            </span>
                                          </div>

                                          {settlement.allocations.length > 0 && (
                                            <p className="text-[10px] text-foreground-500 mt-2">
                                              Rateio: {settlement.allocations.length}{' '}
                                              {settlement.allocations.length === 1
                                                ? 'parcela'
                                                : 'parcelas'}
                                            </p>
                                          )}

                                          {settlement.receiptReference && (
                                            <p className="text-[10px] text-foreground-500 mt-1">
                                              <strong>Referência:</strong>{' '}
                                              {settlement.receiptReference}
                                            </p>
                                          )}

                                          {settlement.notes && (
                                            <p className="text-[10px] text-foreground-500 mt-1">
                                              <strong>
                                                {settlement.kind === 'reversal'
                                                  ? 'Motivo'
                                                  : 'Observação'}:
                                              </strong>{' '}
                                              {settlement.notes}
                                            </p>
                                          )}

                                          {settlement.kind === 'reversal' && settlement.reversalOfId && (
                                            <p className="text-[9px] text-red-500 mt-1">
                                              Vinculado à baixa original.
                                            </p>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="p-3 bg-background-50 border border-dashed border-background-300 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <i className="ri-link text-foreground-400"></i>
              <span className="text-xs text-foreground-500">Asaas não conectado - Status via API futuramente</span>
            </div>
            <button className="text-xs font-medium text-primary-600 hover:text-primary-700 cursor-pointer whitespace-nowrap">
              Conectar Asaas
            </button>
          </div>
        </div>

        <Drawer
          isOpen={showStatementEntryDrawer}
          onClose={closeStatementEntryDrawer}
          title="Adicionar Lançamento do Extrato"
          width="w-[520px]"
          footer={
            <div className="flex items-center gap-2">
              <button
                onClick={closeStatementEntryDrawer}
                disabled={isSavingStatementEntry}
                className="px-3 py-1.5 rounded-lg text-xs font-medium border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-60"
              >
                Cancelar
              </button>

              <button
                onClick={() =>
                  void handleSaveStatementEntry()
                }
                disabled={isSavingStatementEntry}
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-primary-500 text-white hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-60"
              >
                {isSavingStatementEntry
                  ? 'Salvando...'
                  : 'Adicionar'}
              </button>
            </div>
          }
        >
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">
                Conta financeira *
              </label>

              <select
                value={statementAccountId}
                onChange={(e) =>
                  setStatementAccountId(
                    e.target.value
                  )
                }
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm cursor-pointer"
              >
                <option value="">
                  Selecione
                </option>

                {financialAccounts.map(
                  (account) => (
                    <option
                      key={account.id}
                      value={account.id}
                    >
                      {account.name}
                    </option>
                  )
                )}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">
                  Tipo *
                </label>

                <select
                  value={statementDirection}
                  onChange={(e) =>
                    setStatementDirection(
                      e.target.value as
                        'credit' | 'debit'
                    )
                  }
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm cursor-pointer"
                >
                  <option value="credit">
                    Crédito / Entrada
                  </option>

                  <option value="debit">
                    Débito / Saída
                  </option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">
                  Valor (R$) *
                </label>

                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={statementAmount}
                  onChange={(e) =>
                    setStatementAmount(
                      e.target.value
                    )
                  }
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">
                Data *
              </label>

              <input
                type="date"
                value={statementPostedAt}
                onChange={(e) =>
                  setStatementPostedAt(
                    e.target.value
                  )
                }
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">
                Descrição *
              </label>

              <input
                type="text"
                value={statementDescription}
                onChange={(e) =>
                  setStatementDescription(
                    e.target.value
                  )
                }
                placeholder="Descrição que consta no extrato"
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">
                Referência
              </label>

              <input
                type="text"
                value={statementReference}
                onChange={(e) =>
                  setStatementReference(
                    e.target.value
                  )
                }
                placeholder="ID, documento ou referência bancária"
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">
                Saldo após o lançamento
              </label>

              <input
                type="number"
                step="0.01"
                value={statementBalanceAfter}
                onChange={(e) =>
                  setStatementBalanceAfter(
                    e.target.value
                  )
                }
                placeholder="Opcional"
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm"
              />
            </div>

            <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-xs text-blue-700">
              Esta entrada manual usa a mesma estrutura preparada para futuras importações CSV, OFX e integração bancária.
            </div>
          </div>
        </Drawer>

        <Drawer
          isOpen={showReconciliationDrawer}
          onClose={closeReconciliationDrawer}
          title="Conciliar Lançamento"
          width="w-[620px]"
        >
          {!selectedStatementEntry ? (
            <p className="text-xs text-foreground-500">
              Nenhum lançamento selecionado.
            </p>
          ) : (
            <div className="space-y-4">
              <div className="p-3 rounded-xl border border-background-200 bg-background-100/50">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-foreground-800">
                      {selectedStatementEntry.description}
                    </p>

                    <p className="text-xs text-foreground-500 mt-1">
                      {selectedStatementEntry.accountName}
                      {' · '}
                      {formatDate(
                        selectedStatementEntry.postedAt
                      )}
                    </p>
                  </div>

                  <p
                    className={`text-base font-bold ${
                      selectedStatementEntry.amountCents >= 0
                        ? 'text-emerald-600'
                        : 'text-red-500'
                    }`}
                  >
                    {selectedStatementEntry.amountCents >= 0
                      ? '+ '
                      : '− '}

                    {formatCurrencyCents(
                      Math.abs(
                        selectedStatementEntry.amountCents
                      )
                    )}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-background-200">
                  <div>
                    <p className="text-[10px] uppercase text-foreground-400">
                      Já conciliado
                    </p>

                    <p className="text-xs font-semibold text-foreground-700 mt-0.5">
                      {formatCurrencyCents(
                        selectedStatementEntry.matchedCents
                      )}
                    </p>
                  </div>

                  <div>
                    <p className="text-[10px] uppercase text-foreground-400">
                      Pendente
                    </p>

                    <p className="text-xs font-semibold text-amber-700 mt-0.5">
                      {formatCurrencyCents(
                        selectedStatementEntry.remainingCents
                      )}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-start justify-between gap-3">
                <div>
                  <h4 className="text-xs font-semibold text-foreground-700">
                    {reconciliationCandidateMode === 'automatic'
                      ? 'Sugestões automáticas'
                      : 'Conciliação manual'}
                  </h4>

                  <p className="text-[10px] text-foreground-400 mt-0.5">
                    {reconciliationCandidateMode === 'automatic'
                      ? 'O sistema compara conta, sentido financeiro, valor, data e referências.'
                      : 'Escolha qualquer movimentação disponível da mesma conta e direção financeira.'}
                  </p>
                </div>

                {reconciliationCandidateMode === 'automatic' ? (
                  <button
                    onClick={() =>
                      void handleLoadManualCandidates()
                    }
                    disabled={
                      isLoadingCandidates ||
                      matchingCandidateId !== null
                    }
                    className="text-[10px] px-2.5 py-1.5 rounded-lg bg-background-100 text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer disabled:opacity-60"
                  >
                    <i className="ri-search-line mr-1"></i>
                    Busca manual
                  </button>
                ) : (
                  <button
                    onClick={() =>
                      void handleLoadAutomaticCandidates()
                    }
                    disabled={
                      isLoadingCandidates ||
                      matchingCandidateId !== null
                    }
                    className="text-[10px] px-2.5 py-1.5 rounded-lg bg-primary-50 text-primary-700 hover:bg-primary-100 transition-colors cursor-pointer disabled:opacity-60"
                  >
                    <i className="ri-magic-line mr-1"></i>
                    Sugestões automáticas
                  </button>
                )}
              </div>

              {reconciliationCandidateMode === 'manual' && (
                <input
                  type="text"
                  value={reconciliationManualSearch}
                  onChange={(e) =>
                    setReconciliationManualSearch(
                      e.target.value
                    )
                  }
                  placeholder="Buscar por descrição ou referência..."
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-xs"
                />
              )}

              {isLoadingCandidates ? (
                <p className="text-xs text-foreground-500 py-6 text-center">
                  Procurando correspondências...
                </p>
              ) : visibleReconciliationCandidates.length === 0 ? (
                <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
                  <p className="text-xs text-amber-700">
                    {reconciliationCandidateMode === 'automatic'
                      ? 'Nenhuma correspondência automática encontrada.'
                      : 'Nenhuma movimentação encontrada para esta busca.'}
                  </p>

                  {reconciliationCandidateMode === 'automatic' && (
                    <button
                      onClick={() =>
                        void handleLoadManualCandidates()
                      }
                      className="mt-2 text-[10px] font-medium text-amber-700 hover:text-amber-800 cursor-pointer"
                    >
                      Procurar manualmente
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {visibleReconciliationCandidates.map(
                    (candidate) => (
                      <div
                        key={`${candidate.targetType}-${candidate.targetId}`}
                        className="border border-background-200 rounded-xl p-3"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-xs font-semibold text-foreground-800">
                                {candidate.description}
                              </p>

                              {reconciliationCandidateMode === 'automatic' ? (
                                <span
                                  className={`text-[9px] px-1.5 py-0.5 rounded-full ${
                                    candidate.confidence >= 90
                                      ? 'bg-emerald-50 text-emerald-700'
                                      : candidate.confidence >= 75
                                        ? 'bg-blue-50 text-blue-700'
                                        : 'bg-amber-50 text-amber-700'
                                  }`}
                                >
                                  {candidate.confidence}% de confiança
                                </span>
                              ) : (
                                <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-background-100 text-foreground-600">
                                  Seleção manual
                                </span>
                              )}
                            </div>

                            <p className="text-[10px] text-foreground-400 mt-1">
                              {candidate.targetType === 'payment_settlement'
                                ? 'Baixa de cobrança'
                                : 'Livro Caixa'}

                              {' · '}

                              {formatDate(
                                candidate.occurredAt
                              )}

                              {candidate.reference
                                ? ` · Ref.: ${candidate.reference}`
                                : ''}
                            </p>
                          </div>

                          <div className="text-right">
                            <p
                              className={`text-sm font-bold ${
                                candidate.amountCents >= 0
                                  ? 'text-emerald-600'
                                  : 'text-red-500'
                              }`}
                            >
                              {candidate.amountCents >= 0
                                ? '+ '
                                : '− '}

                              {formatCurrencyCents(
                                Math.abs(
                                  candidate.amountCents
                                )
                              )}
                            </p>

                            {candidate.matchedCents > 0 && (
                              <p className="text-[10px] text-foreground-400 mt-0.5">
                                Disponível:{' '}
                                {formatCurrencyCents(
                                  candidate.remainingCents
                                )}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="flex justify-end mt-3">
                          <button
                            onClick={() =>
                              void handleReconcileCandidate(
                                candidate
                              )
                            }
                            disabled={
                              matchingCandidateId !==
                                null
                            }
                            className="text-[10px] px-3 py-1.5 rounded-lg bg-primary-500 text-white hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                          >
                            <i className="ri-links-line mr-1"></i>

                            {matchingCandidateId ===
                            candidate.targetId
                              ? 'Conciliando...'
                              : reconciliationCandidateMode === 'manual'
                                ? 'Conciliar manualmente'
                                : `Conciliar ${formatCurrencyCents(
                                    Math.min(
                                      selectedStatementEntry.remainingCents,
                                      candidate.remainingCents
                                    )
                                  )}`}
                          </button>
                        </div>
                      </div>
                    )
                  )}
                </div>
              )}
            </div>
          )}
        </Drawer>

        <Drawer
          isOpen={showFinancialMovementDrawer}
          onClose={closeFinancialMovementDrawer}
          title="Nova Movimentação"
          width="w-[560px]"
          footer={
            <div className="flex items-center gap-2">
              <button
                onClick={closeFinancialMovementDrawer}
                disabled={isSavingFinancialMovement}
                className="px-3 py-1.5 rounded-lg text-xs font-medium border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-60"
              >
                Cancelar
              </button>

              <button
                onClick={() =>
                  void handleSaveFinancialMovement()
                }
                disabled={
                  isSavingFinancialMovement ||
                  isLoadingFinancialCategories
                }
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-primary-500 text-white hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-60"
              >
                {isSavingFinancialMovement
                  ? 'Registrando...'
                  : 'Registrar'}
              </button>
            </div>
          }
        >
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">
                  Tipo *
                </label>

                <select
                  value={financialMovementForm.transactionType}
                  onChange={(e) => {
                    const transactionType =
                      e.target.value as
                        FinancialMovementFormState['transactionType'];

                    const firstCategory =
                      financialCategories.find(
                        (category) =>
                          transactionType !== 'transfer' &&
                          (
                            category.categoryType ===
                              transactionType ||
                            category.categoryType ===
                              'both'
                          )
                      );

                    const destination =
                      financialAccounts.find(
                        (account) =>
                          account.id !==
                          financialMovementForm.accountId
                      );

                    setFinancialMovementForm(
                      (prev) => ({
                        ...prev,

                        transactionType,

                        categoryId:
                          firstCategory?.id ??
                          '',

                        destinationAccountId:
                          transactionType === 'transfer'
                            ? (
                                prev.destinationAccountId &&
                                prev.destinationAccountId !==
                                  prev.accountId
                                  ? prev.destinationAccountId
                                  : destination?.id ?? ''
                              )
                            : '',
                      })
                    );
                  }}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm cursor-pointer"
                >
                  {financialTransactionTypeOptions.map(
                    (option) => (
                      <option
                        key={option.value}
                        value={option.value}
                      >
                        {option.label}
                      </option>
                    )
                  )}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">
                  Valor (R$) *
                </label>

                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={financialMovementForm.amount}
                  onChange={(e) =>
                    setFinancialMovementForm(
                      (prev) => ({
                        ...prev,
                        amount:
                          e.target.value,
                      })
                    )
                  }
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm"
                />
              </div>
            </div>

            <div className={`grid ${
              financialMovementForm.transactionType === 'transfer'
                ? 'grid-cols-2'
                : 'grid-cols-1'
            } gap-3`}>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">
                  {financialMovementForm.transactionType === 'transfer'
                    ? 'Conta de origem *'
                    : 'Conta *'}
                </label>

                <select
                  value={financialMovementForm.accountId}
                  onChange={(e) => {
                    const accountId =
                      e.target.value;

                    const destination =
                      financialAccounts.find(
                        (account) =>
                          account.id !==
                          accountId
                      );

                    setFinancialMovementForm(
                      (prev) => ({
                        ...prev,

                        accountId,

                        destinationAccountId:
                          prev.destinationAccountId ===
                            accountId
                            ? destination?.id ??
                              ''
                            : prev.destinationAccountId,
                      })
                    );
                  }}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm cursor-pointer"
                >
                  <option value="">
                    Selecione
                  </option>

                  {financialAccounts.map(
                    (account) => (
                      <option
                        key={account.id}
                        value={account.id}
                      >
                        {account.name} ·{' '}
                        {formatCurrencyCents(
                          account.currentBalanceCents
                        )}
                      </option>
                    )
                  )}
                </select>
              </div>

              {financialMovementForm.transactionType ===
                'transfer' && (
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">
                    Conta de destino *
                  </label>

                  <select
                    value={financialMovementForm.destinationAccountId}
                    onChange={(e) =>
                      setFinancialMovementForm(
                        (prev) => ({
                          ...prev,

                          destinationAccountId:
                            e.target.value,
                        })
                      )
                    }
                    className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm cursor-pointer"
                  >
                    <option value="">
                      Selecione
                    </option>

                    {financialAccounts
                      .filter(
                        (account) =>
                          account.id !==
                          financialMovementForm.accountId
                      )
                      .map(
                        (account) => (
                          <option
                            key={account.id}
                            value={account.id}
                          >
                            {account.name} ·{' '}
                            {formatCurrencyCents(
                              account.currentBalanceCents
                            )}
                          </option>
                        )
                      )}
                  </select>
                </div>
              )}
            </div>

            {financialMovementForm.transactionType !==
              'transfer' && (
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">
                  Categoria *
                </label>

                <select
                  value={financialMovementForm.categoryId}
                  onChange={(e) =>
                    setFinancialMovementForm(
                      (prev) => ({
                        ...prev,
                        categoryId:
                          e.target.value,
                      })
                    )
                  }
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm cursor-pointer"
                >
                  <option value="">
                    Selecione
                  </option>

                  {financialMovementCategories.map(
                    (category) => (
                      <option
                        key={category.id}
                        value={category.id}
                      >
                        {category.name}
                      </option>
                    )
                  )}
                </select>
              </div>
            )}

            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">
                Descrição *
              </label>

              <input
                type="text"
                value={financialMovementForm.description}
                onChange={(e) =>
                  setFinancialMovementForm(
                    (prev) => ({
                      ...prev,
                      description:
                        e.target.value,
                    })
                  )
                }
                placeholder="Ex.: mensalidade do software jurídico"
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">
                  Data *
                </label>

                <input
                  type="date"
                  value={financialMovementForm.occurredAt}
                  onChange={(e) =>
                    setFinancialMovementForm(
                      (prev) => ({
                        ...prev,
                        occurredAt:
                          e.target.value,
                      })
                    )
                  }
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">
                  Referência
                </label>

                <input
                  type="text"
                  value={financialMovementForm.reference}
                  onChange={(e) =>
                    setFinancialMovementForm(
                      (prev) => ({
                        ...prev,
                        reference:
                          e.target.value,
                      })
                    )
                  }
                  placeholder="NF, comprovante, identificação..."
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">
                  Cliente
                </label>

                <select
                  value={financialMovementForm.clientId}
                  onChange={(e) =>
                    setFinancialMovementForm(
                      (prev) => ({
                        ...prev,

                        clientId:
                          e.target.value,

                        caseId: '',
                      })
                    )
                  }
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm cursor-pointer"
                >
                  <option value="">
                    Sem vínculo
                  </option>

                  {clients.map(
                    (client) => (
                      <option
                        key={client.id}
                        value={client.id}
                      >
                        {client.name}
                      </option>
                    )
                  )}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">
                  Caso
                </label>

                <select
                  value={financialMovementForm.caseId}
                  onChange={(e) =>
                    setFinancialMovementForm(
                      (prev) => ({
                        ...prev,
                        caseId:
                          e.target.value,
                      })
                    )
                  }
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm cursor-pointer"
                >
                  <option value="">
                    Sem vínculo
                  </option>

                  {financialMovementCaseOptions.map(
                    (caseItem) => (
                      <option
                        key={caseItem.id}
                        value={caseItem.id}
                      >
                        {caseItem.title}
                      </option>
                    )
                  )}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">
                Observações
              </label>

              <textarea
                rows={3}
                value={financialMovementForm.notes}
                onChange={(e) =>
                  setFinancialMovementForm(
                    (prev) => ({
                      ...prev,
                      notes:
                        e.target.value,
                    })
                  )
                }
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm resize-none"
              />
            </div>

            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
              O lançamento altera automaticamente o saldo das contas. Movimentações não serão apagadas; correções financeiras serão registradas por estorno.
            </div>
          </div>
        </Drawer>

        <Drawer
          isOpen={showFinancialAccountDrawer}
          onClose={closeFinancialAccountDrawer}
          title={
            financialAccountBeingEdited
              ? 'Editar Conta Financeira'
              : 'Nova Conta Financeira'
          }
          width="w-[500px]"
          footer={
            <div className="flex items-center gap-2">
              <button
                onClick={closeFinancialAccountDrawer}
                disabled={isSavingFinancialAccount}
                className="px-3 py-1.5 rounded-lg text-xs font-medium border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-60"
              >
                Cancelar
              </button>

              <button
                onClick={() => void handleSaveFinancialAccount()}
                disabled={isSavingFinancialAccount}
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-primary-500 text-white hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-60"
              >
                {isSavingFinancialAccount
                  ? 'Salvando...'
                  : 'Salvar Conta'}
              </button>
            </div>
          }
        >
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">
                Nome da conta *
              </label>

              <input
                type="text"
                value={financialAccountForm.name}
                onChange={(e) =>
                  setFinancialAccountForm((prev) => ({
                    ...prev,
                    name: e.target.value,
                  }))
                }
                placeholder="Ex.: Banco Inter"
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm focus:outline-none focus:border-primary-300"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">
                  Tipo *
                </label>

                <select
                  value={financialAccountForm.accountType}
                  onChange={(e) =>
                    setFinancialAccountForm((prev) => ({
                      ...prev,
                      accountType: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm cursor-pointer"
                >
                  {financialAccountTypeOptions.map((option) => (
                    <option
                      key={option.value}
                      value={option.value}
                    >
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">
                  Instituição
                </label>

                <input
                  type="text"
                  value={financialAccountForm.institution}
                  onChange={(e) =>
                    setFinancialAccountForm((prev) => ({
                      ...prev,
                      institution: e.target.value,
                    }))
                  }
                  placeholder="Ex.: Banco Inter"
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">
                  Saldo inicial (R$)
                </label>

                <input
                  type="number"
                  step="0.01"
                  value={financialAccountForm.openingBalance}
                  disabled={
                    Boolean(
                      financialAccountBeingEdited &&
                      financialAccountBeingEdited.movementCount > 0
                    )
                  }
                  onChange={(e) =>
                    setFinancialAccountForm((prev) => ({
                      ...prev,
                      openingBalance: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm disabled:opacity-60"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">
                  Data do saldo inicial
                </label>

                <input
                  type="date"
                  value={financialAccountForm.openingBalanceDate}
                  disabled={
                    Boolean(
                      financialAccountBeingEdited &&
                      financialAccountBeingEdited.movementCount > 0
                    )
                  }
                  onChange={(e) =>
                    setFinancialAccountForm((prev) => ({
                      ...prev,
                      openingBalanceDate: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm disabled:opacity-60"
                />
              </div>
            </div>

            {financialAccountBeingEdited &&
              financialAccountBeingEdited.movementCount > 0 && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
                  Esta conta já possui movimentações. O saldo inicial foi bloqueado para preservar a integridade do histórico financeiro.
                </div>
              )}

            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">
                Observações
              </label>

              <textarea
                rows={3}
                value={financialAccountForm.notes}
                onChange={(e) =>
                  setFinancialAccountForm((prev) => ({
                    ...prev,
                    notes: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm resize-none"
              />
            </div>

            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
              O saldo atual não é digitado manualmente. Ele é calculado a partir do saldo inicial, recebimentos e estornos registrados no sistema.
            </div>
          </div>
        </Drawer>

        <Drawer
          isOpen={showSettlementDrawer}
          onClose={closeSettlementDrawer}
          title="Registrar Baixa"
          width="w-[520px]"
          footer={
            <div className="flex items-center gap-2">
              <button
                onClick={closeSettlementDrawer}
                disabled={isSavingSettlement}
                className="px-3 py-1.5 rounded-lg text-xs font-medium border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                Cancelar
              </button>

              <button
                onClick={() => void handleSaveSettlement()}
                disabled={
                  isSavingSettlement ||
                  isLoadingFinancialAccounts
                }
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isSavingSettlement
                  ? 'Registrando...'
                  : 'Confirmar Baixa'}
              </button>
            </div>
          }
        >
          <div className="space-y-4">
            {settlementPayment && (
              <div className="p-3 bg-background-100 rounded-lg">
                <p className="text-xs font-semibold text-foreground-800">
                  {settlementPayment.clientName ?? 'Cliente'}
                </p>

                <p className="text-xs text-foreground-500 mt-1">
                  {settlementPayment.title}
                </p>

                <div className="flex items-center justify-between mt-2 text-xs">
                  <span className="text-foreground-500">
                    Saldo da cobrança
                  </span>

                  <span className="font-semibold text-amber-700">
                    {formatCurrencyCents(
                      settlementPayment.balanceAmountCents ?? 0
                    )}
                  </span>
                </div>
              </div>
            )}

            {settlementForm.installmentId && settlementPayment && (() => {
              const installment =
                settlementPayment.installments.find(
                  (item) =>
                    item.id === settlementForm.installmentId
                );

              if (!installment) {
                return null;
              }

              const balanceCents =
                Math.max(
                  0,
                  installment.amountCents -
                    (installment.paidAmountCents ?? 0)
                );

              return (
                <div className="p-3 border border-background-200 rounded-lg">
                  <p className="text-xs text-foreground-500">
                    Parcela selecionada
                  </p>

                  <p className="text-sm font-semibold text-foreground-800 mt-1">
                    {installment.installmentNumber}ª parcela ·{' '}
                    {formatCurrencyCents(balanceCents)} em aberto
                  </p>
                </div>
              );
            })()}

            {financialAccountsError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
                {financialAccountsError}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">
                  Valor da baixa (R$) *
                </label>

                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={settlementForm.amount}
                  onChange={(e) =>
                    setSettlementForm((prev) => ({
                      ...prev,
                      amount: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">
                  Data do recebimento *
                </label>

                <input
                  type="date"
                  value={settlementForm.occurredAt}
                  onChange={(e) =>
                    setSettlementForm((prev) => ({
                      ...prev,
                      occurredAt: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">
                  Forma de pagamento *
                </label>

                <select
                  value={settlementForm.paymentMethod}
                  onChange={(e) =>
                    setSettlementForm((prev) => ({
                      ...prev,
                      paymentMethod: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
                >
                  {paymentMethodOptions.map((option) => (
                    <option
                      key={option.value}
                      value={option.value}
                    >
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">
                  Conta de destino *
                </label>

                <select
                  value={settlementForm.financialAccountId}
                  onChange={(e) =>
                    setSettlementForm((prev) => ({
                      ...prev,
                      financialAccountId: e.target.value,
                    }))
                  }
                  disabled={isLoadingFinancialAccounts}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer disabled:opacity-60"
                >
                  <option value="">
                    {isLoadingFinancialAccounts
                      ? 'Carregando contas...'
                      : 'Selecione'}
                  </option>

                  {financialAccounts.map((account) => (
                    <option
                      key={account.id}
                      value={account.id}
                    >
                      {account.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">
                Comprovante / referência
              </label>

              <input
                type="text"
                value={settlementForm.receiptReference}
                onChange={(e) =>
                  setSettlementForm((prev) => ({
                    ...prev,
                    receiptReference: e.target.value,
                  }))
                }
                placeholder="Ex.: PIX 23/08, recibo, identificação bancária..."
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">
                Observações
              </label>

              <textarea
                value={settlementForm.notes}
                onChange={(e) =>
                  setSettlementForm((prev) => ({
                    ...prev,
                    notes: e.target.value,
                  }))
                }
                rows={3}
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 resize-none focus:outline-none focus:border-primary-300"
              />
            </div>

            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
              A baixa ficará registrada no histórico financeiro e não será apagada por alterações posteriores da cobrança.
            </div>
          </div>
        </Drawer>

        <Drawer
          isOpen={showNewCharge}
          onClose={closeNewChargeDrawer}
          title={paymentBeingEdited ? 'Editar Cobrança' : 'Nova Cobrança'}
          width="w-[540px]"
          footer={
            <div className="flex items-center gap-2">
              <button
                onClick={closeNewChargeDrawer}
                className="px-3 py-1.5 rounded-lg text-xs font-medium border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveCharge}
                disabled={isSavingPayment}
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isSavingPayment ? 'Salvando...' : paymentBeingEdited ? 'Atualizar Cobrança' : 'Criar Cobrança'}
              </button>
            </div>
          }
        >
          <div className="space-y-4">
            {!clientsLoading && clients.length === 0 ? (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                Cadastre um cliente antes de criar um lançamento financeiro.
              </div>
            ) : null}

            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Título *</label>
              <input
                type="text"
                value={newCharge.title}
                onChange={(e) => setNewCharge((prev) => ({ ...prev, title: e.target.value }))}
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                placeholder="Ex: Honorários contratados — Fraude Pix"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Descrição</label>
              <textarea
                value={newCharge.description}
                onChange={(e) => setNewCharge((prev) => ({ ...prev, description: e.target.value }))}
                rows={2}
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 resize-none focus:outline-none focus:border-primary-300"
                placeholder="Detalhes do lançamento financeiro..."
              ></textarea>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Cliente *</label>
                <select
                  value={newCharge.clientId}
                  onChange={(e) =>
                    setNewCharge((prev) => ({
                      ...prev,
                      clientId: e.target.value,
                      caseId: '',

                      sucumbencyBasisAmount:
                        prev.sucumbencyBasisType ===
                          'case_value'
                          ? ''
                          : prev.sucumbencyBasisAmount,
                    }))
                  }
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
                  disabled={clientsLoading}
                >
                  <option value="">{clientsLoading ? 'Carregando clientes...' : 'Selecione um cliente'}</option>
                  {clientOptions.map((client) => (
                    <option key={client.value} value={client.value}>
                      {client.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Caso</label>
                <select
                  value={newCharge.caseId}
                  onChange={(e) => {
                    const caseId =
                      e.target.value;

                    const selectedCase =
                      cases.find(
                        (caseItem) =>
                          caseItem.id ===
                          caseId
                      );

                    setNewCharge(
                      (prev) => ({
                        ...prev,

                        caseId,

                        sucumbencyBasisAmount:
                          prev.chargeType ===
                              'sucumbency' &&
                            prev.sucumbencyCalculationType ===
                              'percentage' &&
                            prev.sucumbencyBasisType ===
                              'case_value' &&
                            selectedCase?.caseValue !==
                              null &&
                            selectedCase?.caseValue !==
                              undefined
                            ? selectedCase.caseValue.toFixed(
                                2
                              )
                            : prev.sucumbencyBasisAmount,
                      })
                    );
                  }}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
                  disabled={isLoadingCases}
                >
                  <option value="">{isLoadingCases ? 'Carregando casos...' : 'Selecione um caso'}</option>
                  {caseOptions.map((caseItem) => (
                    <option key={caseItem.value} value={caseItem.value}>
                      {caseItem.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">
                Natureza da cobrança
              </label>

              <select
                value={newCharge.chargeType}
                onChange={(e) => {
                  const chargeType =
                    e.target.value as
                      PaymentChargeType;

                  const selectedCase =
                    cases.find(
                      (caseItem) =>
                        caseItem.id ===
                        newCharge.caseId
                    );

                  setNewCharge(
                    (prev) => ({
                      ...prev,

                      chargeType,

                      successFeePercentage:
                        chargeType ===
                          'sucumbency'
                          ? ''
                          : prev.successFeePercentage,

                      sucumbencyBasisAmount:
                        chargeType ===
                            'sucumbency' &&
                          prev.sucumbencyCalculationType ===
                            'percentage' &&
                          prev.sucumbencyBasisType ===
                            'case_value' &&
                          selectedCase?.caseValue !==
                            null &&
                          selectedCase?.caseValue !==
                            undefined
                          ? selectedCase.caseValue.toFixed(
                              2
                            )
                          : prev.sucumbencyBasisAmount,
                    })
                  );
                }}
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
              >
                <option value="contractual_fee">
                  Honorários contratuais
                </option>

                <option value="success_fee">
                  Honorários de êxito
                </option>

                <option value="sucumbency">
                  Sucumbência
                </option>

                <option value="reimbursement">
                  Reembolso
                </option>

                <option value="other">
                  Outros
                </option>
              </select>
            </div>

            {newCharge.chargeType ===
              'sucumbency' && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
                <div>
                  <p className="text-xs font-semibold text-blue-800">
                    Definição da sucumbência
                  </p>

                  <p className="text-[10px] text-blue-700 mt-1">
                    A base abaixo é uma fotografia editável desta cobrança e não será alterada automaticamente se o valor cadastrado no processo mudar.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-foreground-600 mb-1">
                      Forma de definição
                    </label>

                    <select
                      value={
                        newCharge.sucumbencyCalculationType
                      }
                      onChange={(e) =>
                        setNewCharge(
                          (prev) => ({
                            ...prev,

                            sucumbencyCalculationType:
                              e.target.value as
                                SucumbencyCalculationType,
                          })
                        )
                      }
                      className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm cursor-pointer"
                    >
                      <option value="percentage">
                        Percentual sobre uma base
                      </option>

                      <option value="fixed">
                        Valor fixado / arbitrado
                      </option>
                    </select>
                  </div>

                  {newCharge.sucumbencyCalculationType ===
                    'percentage' && (
                    <div>
                      <label className="block text-xs font-medium text-foreground-600 mb-1">
                        Base de cálculo
                      </label>

                      <select
                        value={
                          newCharge.sucumbencyBasisType
                        }
                        onChange={(e) => {
                          const basisType =
                            e.target.value as
                              SucumbencyBasisType;

                          const selectedCase =
                            cases.find(
                              (caseItem) =>
                                caseItem.id ===
                                newCharge.caseId
                            );

                          setNewCharge(
                            (prev) => ({
                              ...prev,

                              sucumbencyBasisType:
                                basisType,

                              sucumbencyBasisAmount:
                                basisType ===
                                    'case_value' &&
                                  selectedCase?.caseValue !==
                                    null &&
                                  selectedCase?.caseValue !==
                                    undefined
                                  ? selectedCase.caseValue.toFixed(
                                      2
                                    )
                                  : prev.sucumbencyBasisAmount,
                            })
                          );
                        }}
                        className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm cursor-pointer"
                      >
                        <option value="case_value">
                          Valor da causa
                        </option>

                        <option value="judgment_amount">
                          Valor da condenação
                        </option>

                        <option value="economic_benefit">
                          Proveito econômico
                        </option>

                        <option value="manual">
                          Outra base / manual
                        </option>
                      </select>
                    </div>
                  )}
                </div>

                {newCharge.sucumbencyCalculationType ===
                  'percentage' && (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-foreground-600 mb-1">
                          Percentual da sucumbência (%)
                        </label>

                        <input
                          type="number"
                          step="0.01"
                          min="0.01"
                          max="100"
                          value={
                            newCharge.sucumbencyPercentage
                          }
                          onChange={(e) =>
                            setNewCharge(
                              (prev) => ({
                                ...prev,

                                sucumbencyPercentage:
                                  e.target.value,
                              })
                            )
                          }
                          className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-foreground-600 mb-1">
                          Valor-base (R$) — editável
                        </label>

                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={
                            newCharge.sucumbencyBasisAmount
                          }
                          onChange={(e) =>
                            setNewCharge(
                              (prev) => ({
                                ...prev,

                                sucumbencyBasisAmount:
                                  e.target.value,
                              })
                            )
                          }
                          className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm"
                        />
                      </div>
                    </div>

                    {newCharge.sucumbencyBasisType ===
                        'case_value' &&
                      newCharge.caseId && (
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-[10px] text-blue-700">
                          O valor foi sugerido a partir do caso, mas pode ser corrigido livremente.
                        </p>

                        <button
                          type="button"
                          onClick={() => {
                            const selectedCase =
                              cases.find(
                                (caseItem) =>
                                  caseItem.id ===
                                  newCharge.caseId
                              );

                            if (
                              selectedCase?.caseValue !==
                                null &&
                              selectedCase?.caseValue !==
                                undefined
                            ) {
                              setNewCharge(
                                (prev) => ({
                                  ...prev,

                                  sucumbencyBasisAmount:
                                    selectedCase.caseValue.toFixed(
                                      2
                                    ),
                                })
                              );
                            }
                          }}
                          className="text-[10px] font-medium text-primary-700 hover:text-primary-800 cursor-pointer"
                        >
                          Usar valor atual do caso
                        </button>
                      </div>
                    )}

                    <div className="flex items-center justify-between p-2.5 bg-background-50 rounded-lg border border-blue-100">
                      <span className="text-xs text-foreground-600">
                        Sucumbência calculada
                      </span>

                      <strong className="text-sm text-blue-800">
                        {formatCurrencyCents(
                          resolvePaymentFormTotalCents(
                            newCharge
                          ) ?? 0
                        )}
                      </strong>
                    </div>
                  </>
                )}

                {newCharge.sucumbencyCalculationType ===
                  'fixed' && (
                  <p className="text-[10px] text-blue-700">
                    Informe abaixo o valor efetivamente fixado ou arbitrado pelo juízo.
                  </p>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Regra de Honorários</label>
                <select
                  value={newCharge.feeRuleId}
                  onChange={(e) => setNewCharge((prev) => ({ ...prev, feeRuleId: e.target.value }))}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
                  disabled={isLoadingFeeRules}
                >
                  <option value="">{isLoadingFeeRules ? 'Carregando regras...' : 'Selecione uma regra'}</option>
                  {feeRuleOptions.map((feeRule) => (
                    <option key={feeRule.value} value={feeRule.value}>
                      {feeRule.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">
                  {newCharge.chargeType === 'sucumbency'
                    ? newCharge.sucumbencyCalculationType === 'percentage'
                      ? 'Sucumbência calculada (R$)'
                      : 'Valor da sucumbência (R$)'
                    : 'Valor Total (R$)'}
                </label>

                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={
                    newCharge.chargeType === 'sucumbency' &&
                    newCharge.sucumbencyCalculationType === 'percentage'
                      ? (
                          resolvePaymentFormTotalCents(
                            newCharge
                          ) === null
                            ? ''
                            : centsToDecimalString(
                                resolvePaymentFormTotalCents(
                                  newCharge
                                ) ?? 0
                              )
                        )
                      : newCharge.totalAmount || ''
                  }
                  disabled={
                    newCharge.chargeType === 'sucumbency' &&
                    newCharge.sucumbencyCalculationType === 'percentage'
                  }
                  onChange={(e) =>
                    setNewCharge(
                      (prev) => ({
                        ...prev,
                        totalAmount:
                          e.target.value,
                      })
                    )
                  }
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 disabled:bg-background-100 disabled:text-foreground-500"
                />
              </div>
            </div>

            <div
              className={`grid gap-3 ${
                newCharge.chargeType === 'sucumbency'
                  ? 'grid-cols-2'
                  : 'grid-cols-3'
              }`}
            >
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Entrada (R$)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={newCharge.entryAmount || ''}
                  onChange={(e) => setNewCharge((prev) => ({ ...prev, entryAmount: e.target.value }))}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Nº Parcelas</label>
                <input
                  type="number"
                  value={newCharge.installmentCount}
                  min={1}
                  max={24}
                  onChange={(e) =>
                    setNewCharge((prev) => ({ ...prev, installmentCount: Math.max(1, Number(e.target.value) || 1).toString() }))
                  }
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                />
              </div>
              {newCharge.chargeType !== 'sucumbency' && (
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">% Êxito</label>
                  <input
                    type="number"
                    value={newCharge.successFeePercentage || ''}
                    onChange={(e) =>
                      setNewCharge(
                        (prev) => ({
                          ...prev,
                          successFeePercentage:
                            e.target.value,
                        })
                      )
                    }
                    className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                  />
                </div>
              )}
            </div>

            <div
              className={`grid gap-3 ${
                newCharge.chargeType === 'sucumbency'
                  ? 'grid-cols-2'
                  : 'grid-cols-3'
              }`}
            >
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Status</label>
                <select
                  value={newCharge.status}
                  onChange={(e) => setNewCharge((prev) => ({ ...prev, status: e.target.value }))}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
                >
                  {paymentStatusFilterOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Método de pagamento</label>
                <select
                  value={newCharge.paymentMethod}
                  onChange={(e) => setNewCharge((prev) => ({ ...prev, paymentMethod: e.target.value }))}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
                >
                  {paymentMethodOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Responsável</label>
                <select
                  value={newCharge.responsibleUser}
                  onChange={(e) => setNewCharge((prev) => ({ ...prev, responsibleUser: e.target.value }))}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
                >
                  <option value="">Selecione</option>
                  {responsibleUserOptions.map((user) => (
                    <option key={user.value} value={user.value}>
                      {user.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Vencimento da primeira parcela</label>
              <input
                type="date"
                value={newCharge.firstDueDate}
                onChange={(e) =>
                  setNewCharge((prev) => ({
                    ...prev,
                    firstDueDate: e.target.value,
                    dueDay: e.target.value ? e.target.value.split('-')[2] : '',
                  }))
                }
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Observações</label>
              <textarea
                value={newCharge.notes}
                onChange={(e) => setNewCharge((prev) => ({ ...prev, notes: e.target.value }))}
                rows={2}
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 resize-none focus:outline-none focus:border-primary-300"
              ></textarea>
            </div>

            {installmentPreview && (
              <div className="p-3 bg-background-100 rounded-lg">
                <p className="text-xs font-semibold text-foreground-600 mb-2">
                  Pré-visualização das parcelas
                </p>

                <div className="space-y-1">
                  {installmentPreview.installments.map(
                    (installmentCents, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between text-xs text-foreground-600"
                      >
                        <span>{index + 1}ª parcela</span>
                        <span className="font-medium">
                          {formatCurrencyCents(installmentCents)}
                        </span>
                      </div>
                    )
                  )}
                </div>

                <div className="flex items-center justify-between text-xs font-semibold text-foreground-800 mt-2 pt-2 border-t border-background-200">
                  <span>Total parcelado</span>
                  <span>
                    {formatCurrencyCents(
                      installmentPreview.totalInstallmentCents
                    )}
                  </span>
                </div>
              </div>
            )}
          </div>
        </Drawer>
      </div>
    </AppLayout>
  );
};

export default FinancePage;
